/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import * as coreClient from "@azure/core-client";

export type AnalyzeActionUnion =
  | AnalyzeAction
  | AnalyzeTextEntityLinkingInput
  | AnalyzeTextEntityRecognitionInput
  | AnalyzeTextKeyPhraseExtractionInput
  | AnalyzeTextPiiEntitiesRecognitionInput
  | AnalyzeTextLanguageDetectionInput
  | AnalyzeTextSentimentAnalysisInput
  | AnalyzeTextDynamicClassificationInput;
export type AnalyzeTextTaskResultUnion =
  | AnalyzeTextTaskResult
  | SentimentTaskResult
  | EntitiesTaskResult
  | EntityLinkingTaskResult
  | PiiTaskResult
  | KeyPhraseTaskResult
  | LanguageDetectionTaskResult
  | DynamicClassificationTaskResult;
export type BaseResolutionUnion =
  | BaseResolution
  | AgeResolution
  | VolumeResolution
  | SpeedResolution
  | AreaResolution
  | LengthResolution
  | InformationResolution
  | TemperatureResolution
  | WeightResolution
  | CurrencyResolution
  | BooleanResolution
  | DateTimeResolution
  | NumberResolution
  | OrdinalResolution
  | TemporalSpanResolution
  | NumericRangeResolution;
export type AnalyzeBatchActionUnion =
  | AnalyzeBatchAction
  | CustomEntitiesLROTask
  | CustomSingleLabelClassificationLROTask
  | CustomMultiLabelClassificationLROTask
  | HealthcareLROTask
  | SentimentAnalysisLROTask
  | EntitiesLROTask
  | EntityLinkingLROTask
  | PiiLROTask
  | ExtractiveSummarizationLROTask
  | KeyPhraseLROTask
  | AbstractiveSummarizationLROTask;
export type AnalyzeTextLROResultUnion =
  | AnalyzeTextLROResult
  | EntityRecognitionLROResult
  | CustomEntityRecognitionLROResult
  | CustomSingleLabelClassificationLROResult
  | CustomMultiLabelClassificationLROResult
  | EntityLinkingLROResult
  | PiiEntityRecognitionLROResult
  | ExtractiveSummarizationLROResult
  | HealthcareLROResult
  | SentimentLROResult
  | KeyPhraseExtractionLROResult
  | AbstractiveSummarizationLROResult;

export interface AnalyzeAction {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind:
    | "EntityLinking"
    | "EntityRecognition"
    | "KeyPhraseExtraction"
    | "PiiEntityRecognition"
    | "LanguageDetection"
    | "SentimentAnalysis"
    | "DynamicClassification";
}

export interface AnalyzeTextTaskResult {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind:
    | "SentimentAnalysisResults"
    | "EntityRecognitionResults"
    | "EntityLinkingResults"
    | "PiiEntityRecognitionResults"
    | "KeyPhraseExtractionResults"
    | "LanguageDetectionResults"
    | "DynamicClassificationResults";
}

/** Error response. */
export interface ErrorResponse {
  /** The error object. */
  error: ErrorModel;
}

/** The error object. */
export interface ErrorModel {
  /** Describes unknown properties. The value of an unknown property can be of "any" type. */
  [property: string]: any;
  /** One of a server-defined set of error codes. */
  code: ErrorCode;
  /** A human-readable representation of the error. */
  message: string;
  /** The target of the error. */
  target?: string;
  /** An array of details about specific errors that led to this reported error. */
  details?: ErrorModel[];
  /** An object containing more specific information than the current object about the error. */
  innererror?: InnerErrorModel;
}

/** An object containing more specific information about the error. As per Microsoft One API guidelines - https://github.com/Microsoft/api-guidelines/blob/vNext/Guidelines.md#7102-error-condition-responses. */
export interface InnerErrorModel {
  /** One of a server-defined set of error codes. */
  code: InnerErrorCode;
  /** Error message. */
  message: string;
  /** Error details. */
  details?: { [propertyName: string]: string };
  /** Error target. */
  target?: string;
  /** An object containing more specific information than the current object about the error. */
  innererror?: InnerErrorModel;
}

export interface AnalyzeTextJobsInput {
  /** Optional display name for the analysis job. */
  displayName?: string;
  /** Default language to use for records requesting automatic language detection. */
  defaultLanguage?: string;
  analysisInput: MultiLanguageAnalysisInput;
  /** The set of tasks to execute on the input documents. */
  tasks: AnalyzeBatchActionUnion[];
}

export interface MultiLanguageAnalysisInput {
  documents?: TextDocumentInput[];
}

/** An object representing an individual text document to be analyzed by the Language service. The document contains a unique document ID, the full text of the document, and the language of the document's text. */
export interface TextDocumentInput {
  /** A unique, non-empty document identifier. */
  id: string;
  /** The input text to process. */
  text: string;
  /** (Optional) This is the 2 letter ISO 639-1 representation of a language. For example, use "en" for English; "es" for Spanish etc. For Auto Language Detection, use "auto". If not set, use "en" for English as default. */
  language?: string;
}

/** The State of a batched action */
export interface BatchActionState {
  /** The name of the action */
  actionName?: string;
}

export interface JobState {
  displayName?: string;
  createdOn: Date;
  expiresOn?: Date;
  id: string;
  modifiedOn: Date;
  status: OperationStatus;
  errors?: ErrorModel[];
  nextLink?: string;
}

export interface TasksState {
  tasks: TasksStateTasks;
}

export interface TasksStateTasks {
  completed: number;
  failed: number;
  inProgress: number;
  total: number;
  items?: AnalyzeTextLROResultUnion[];
}

/** Returns the current state of the task. */
export interface TaskState {
  /** The last updated time in UTC for the task. */
  lastUpdateDateTime: Date;
  /** The status of the task at the mentioned last update time. */
  status: State;
}

export interface AnalyzeTextJobStatistics {
  /** if includeStatistics=true was specified in the request this field will contain information about the request payload. */
  statistics?: TextDocumentBatchStatistics;
}

/** if includeStatistics=true was specified in the request this field will contain information about the request payload. */
export interface TextDocumentBatchStatistics {
  /** Describes unknown properties. The value of an unknown property can be of "any" type. */
  [property: string]: any;
  /** Number of documents submitted in the request. */
  documentCount: number;
  /** Number of valid documents. This excludes empty, over-size limit or non-supported languages documents. */
  validDocumentCount: number;
  /** Number of invalid documents. This includes empty, over-size limit or non-supported languages documents. */
  erroneousDocumentCount: number;
  /** Number of transactions for the request. */
  transactionCount: number;
}

export interface LanguageDetectionAnalysisInput {
  documents?: LanguageDetectionInput[];
}

/** An input to the language detection operation. This object specifies a unique document id, as well as the full text of a document and a hint indicating the document's country of origin to assist the Language predictive model in detecting the document's language. */
export interface LanguageDetectionInput {
  /** Unique, non-empty document identifier. */
  id: string;
  /** Full text of the input document. */
  text: string;
  /** A hint indicating the document's country of origin to assist the Language predictive model in detecting the document's language. */
  countryHint?: string;
}

/** Configuration common to all actions. */
export interface ActionCommon {
  /**
   * If set to true, you opt-out of having your text input logged for troubleshooting. By default, Cognitive Language Service logs your input text for 48 hours, solely to allow for troubleshooting issues. Setting this parameter to true, disables in logging and may limit our ability to remediate issues that occur.
   *
   * Default is false.
   */
  disableServiceLogs?: boolean;
}

export interface CustomResult {
  /** Errors by document id. */
  errors: DocumentError[];
  /** if includeStatistics=true was specified in the request this field will contain information about the request payload. */
  statistics?: TextDocumentBatchStatistics;
  /** This field indicates the project name for the model. */
  projectName: string;
  /** This field indicates the deployment name for the model. */
  deploymentName: string;
}

export interface DocumentError {
  /** Document Id. */
  id: string;
  /** Document Error. */
  error: ErrorModel;
}

/** The abstract base class for entity resolutions. */
export interface BaseResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind:
    | "AgeResolution"
    | "VolumeResolution"
    | "SpeedResolution"
    | "AreaResolution"
    | "LengthResolution"
    | "InformationResolution"
    | "TemperatureResolution"
    | "WeightResolution"
    | "CurrencyResolution"
    | "BooleanResolution"
    | "DateTimeResolution"
    | "NumberResolution"
    | "OrdinalResolution"
    | "TemporalSpanResolution"
    | "NumericRangeResolution";
}

/** A word or phrase identified as an entity that is categorized within a taxonomy of types. The set of categories recognized by the Language service is described at https://docs.microsoft.com/azure/cognitive-services/language-service/named-entity-recognition/concepts/named-entity-categories . */
export interface Entity {
  /** Entity text as appears in the request. */
  text: string;
  /** Entity type. */
  category: string;
  /** (Optional) Entity sub type. */
  subCategory?: string;
  /** Start position for the entity text. Use of different 'stringIndexType' values can affect the offset returned. */
  offset: number;
  /** Length for the entity text. Use of different 'stringIndexType' values can affect the length returned. */
  length: number;
  /** Confidence score between 0 and 1 of the extracted entity. */
  confidenceScore: number;
}

export interface DocumentResult {
  /** Unique, non-empty document identifier. */
  id: string;
  /** Warnings encountered while processing document. */
  warnings: DocumentWarning[];
  /** if showStats=true was specified in the request this field will contain information about the document payload. */
  statistics?: TextDocumentStatistics;
}

/** Represents a warning encountered while processing a document. */
export interface DocumentWarning {
  /** Error code. */
  code: WarningCode;
  /** Warning message. */
  message: string;
}

/** if includeStatistics=true was specified in the request this field will contain information about the document payload. */
export interface TextDocumentStatistics {
  /** Number of text elements recognized in the document. */
  characterCount: number;
  /** Number of transactions for the document. */
  transactionCount: number;
}

/** The auto-detected language of the input document. */
export interface DocumentDetectedLanguage {
  /** If 'language' is set to 'auto' for the document in the request this field will contain a 2 letter ISO 639-1 representation of the language detected for this document. */
  detectedLanguage?: DetectedLanguage;
}

/** Information about the language of a document as identified by the Language service. */
export interface DetectedLanguage {
  /** Long name of a detected language (e.g. English, French). */
  name: string;
  /** A two letter representation of the detected language according to the ISO 639-1 standard (e.g. en, fr). */
  iso6391Name: string;
  /** A confidence score between 0 and 1. Scores close to 1 indicate 100% certainty that the identified language is true. */
  confidenceScore: number;
  /** Identifies the script of the input document. */
  script?: ScriptKind;
}

/** A classification result from a custom classify document single category action */
export interface ClassificationCategory {
  /** Classification type. */
  category: string;
  /** Confidence score between 0 and 1 of the recognized class. */
  confidenceScore: number;
}

/** A type representing a reference for the healthcare entity into a specific entity catalog. */
export interface HealthcareEntity {
  /** Entity text as appears in the request. */
  text: string;
  /** Healthcare Entity Category. */
  category: HealthcareEntityCategory;
  /** (Optional) Entity sub type. */
  subCategory?: string;
  /** Start position for the entity text. Use of different 'stringIndexType' values can affect the offset returned. */
  offset: number;
  /** Length for the entity text. Use of different 'stringIndexType' values can affect the length returned. */
  length: number;
  /** Confidence score between 0 and 1 of the extracted entity. */
  confidenceScore: number;
  /** An object that describes metadata about the healthcare entity such as whether it is hypothetical or conditional. */
  assertion?: HealthcareAssertion;
  /** Preferred name for the entity. Example: 'histologically' would have a 'name' of 'histologic'. */
  normalizedText?: string;
  /** Entity references in known data sources. */
  dataSources?: EntityDataSource[];
}

/** An object that describes metadata about the healthcare entity such as whether it is hypothetical or conditional. */
export interface HealthcareAssertion {
  /** Describes any conditionality on the entity. */
  conditionality?: EntityConditionality;
  /** Describes the entities certainty and polarity. */
  certainty?: EntityCertainty;
  /** Describes if the entity is the subject of the text or if it describes someone else. */
  association?: EntityAssociation;
}

/** A type representing a reference for the healthcare entity into a specific entity catalog. */
export interface EntityDataSource {
  /** Entity Catalog. Examples include: UMLS, CHV, MSH, etc. */
  name: string;
  /** Entity id in the given source catalog. */
  entityId: string;
}

/** Every relation is an entity graph of a certain relationType, where all entities are connected and have specific roles within the relation context. */
export interface HealthcareRelation {
  /** Type of relation. Examples include: `DosageOfMedication` or 'FrequencyOfMedication', etc. */
  relationType: RelationType;
  /** Confidence score between 0 and 1 of the extracted relation. */
  confidenceScore?: number;
  /** The entities in the relation. */
  entities: HealthcareRelationEntity[];
}

export interface HealthcareRelationEntity {
  /** Reference link object, using a JSON pointer RFC 6901 (URI Fragment Identifier Representation), pointing to the entity . */
  ref: string;
  /** Role of entity in the relationship. For example: 'CD20-positive diffuse large B-cell lymphoma' has the following entities with their roles in parenthesis:  CD20 (GeneOrProtein), Positive (Expression), diffuse large B-cell lymphoma (Diagnosis). */
  role: string;
}

export interface PreBuiltResult {
  /** Errors by document id. */
  errors: InputError[];
  /** if includeStatistics=true was specified in the request this field will contain information about the request payload. */
  statistics?: TextDocumentBatchStatistics;
  /** This field indicates which model is used for scoring. */
  modelVersion: string;
}

/** Contains details of errors encountered during a job execution. */
export interface InputError {
  /** The ID of the input. */
  id: string;
  /** Error encountered. */
  error: ErrorModel;
}

/** Represents the confidence scores between 0 and 1 across all sentiment classes: positive, neutral, negative. */
export interface SentimentConfidenceScores {
  /** Confidence score for positive sentiment */
  positive: number;
  /** Confidence score for neutral sentiment */
  neutral: number;
  /** Confidence score for negative sentiment */
  negative: number;
}

/** The predicted sentiment for a given span of text. For more information regarding text sentiment, seehttps://docs.microsoft.com/azure/cognitive-services/language-service/sentiment-opinion-mining/overview. */
export interface SentenceSentiment {
  /** The sentence text. */
  text: string;
  /** The predicted Sentiment for the sentence. */
  sentiment: SentenceSentimentLabel;
  /** The sentiment confidence score between 0 and 1 for the sentence for all classes. */
  confidenceScores: SentimentConfidenceScores;
  /** The sentence offset from the start of the document. */
  offset: number;
  /** The length of the sentence. */
  length: number;
  /** The array of sentence targets for the sentence. */
  targets?: SentenceTarget[];
  /** The array of assessments for the sentence. */
  assessments?: AssessmentSentiment[];
}

export interface SentenceTarget {
  /** Targeted sentiment in the sentence. */
  sentiment: TokenSentimentLabel;
  /** Target sentiment confidence scores for the target in the sentence. */
  confidenceScores: TargetConfidenceScores;
  /** The target offset from the start of the sentence. */
  offset: number;
  /** The length of the target. */
  length: number;
  /** The target text detected. */
  text: string;
  /** The array of either assessment or target objects which is related to the target. */
  relations: TargetRelation[];
}

/** Represents the confidence scores across all sentiment classes: positive and negative. */
export interface TargetConfidenceScores {
  /** Confidence score for positive sentiment */
  positive: number;
  /** Confidence score for negative sentiment */
  negative: number;
}

export interface TargetRelation {
  /** The type related to the target. */
  relationType: TargetRelationType;
  /** The JSON pointer indicating the linked object. */
  ref: string;
}

/** An object that contains the predicted sentiment, confidence scores and other information about an assessment of a target. For example, in the sentence "The food is good", the assessment of the target 'food' is 'good'. */
export interface AssessmentSentiment {
  /** Assessment sentiment in the sentence. */
  sentiment: TokenSentimentLabel;
  /** Assessment sentiment confidence scores in the sentence. */
  confidenceScores: TargetConfidenceScores;
  /** The assessment offset from the start of the sentence. */
  offset: number;
  /** The length of the assessment. */
  length: number;
  /** The assessment text detected. */
  text: string;
  /** The indicator representing if the assessment is negated. */
  isNegated: boolean;
}

/** A word or phrase identified as a well-known entity within a database, including its formal (disambiguated) name and a link to the entity information within the source database. */
export interface LinkedEntity {
  /** Entity Linking formal name. */
  name: string;
  /** List of instances this entity appears in the text. */
  matches: Match[];
  /** Language used in the data source. */
  language: string;
  /** Unique identifier of the recognized entity from the data source. */
  dataSourceEntityId?: string;
  /** URL for the entity's page from the data source. */
  url: string;
  /** Data source used to extract entity linking, such as Wiki/Bing etc. */
  dataSource: string;
  /** Bing Entity Search API unique identifier of the recognized entity. */
  bingEntitySearchApiId?: string;
}

/** Details about the specific substring in a document that refers to a linked entity identified by the Language model. */
export interface Match {
  /** If a well known item is recognized, a decimal number denoting the confidence level between 0 and 1 will be returned. */
  confidenceScore: number;
  /** Entity text as appears in the request. */
  text: string;
  /** Start position for the entity match text. */
  offset: number;
  /** Length for the entity match text. */
  length: number;
}

/** A sentence that is part of the extracted summary. */
export interface SummarySentence {
  /** The extracted sentence text. */
  text: string;
  /** A double value representing the relevance of the sentence within the summary. Higher values indicate higher importance. */
  rankScore: number;
  /** The sentence offset from the start of the document, based on the value of the parameter StringIndexType. */
  offset: number;
  /** The length of the sentence. */
  length: number;
}

export interface Pagination {
  nextLink?: string;
}

/** Supported parameters for an Abstractive Summarization task. */
export interface AbstractiveSummarizationTaskParametersBase {
  /** The max number of sentences to be part of the summary. */
  maxSentenceCount?: number;
  /**
   * Specifies the measurement unit used to calculate the offset and length properties. For a list of possible values, see {@link KnownStringIndexType}.
   *
   * The default is the JavaScript's default which is "Utf16CodeUnit".
   */
  stringIndexType?: StringIndexType;
  /** Control the phrases to be used in the summary. */
  phraseControls?: PhraseControl[];
}

/** Control the phrases to be used in the summary. */
export interface PhraseControl {
  /** The target phrase to control. */
  targetPhrase: string;
  /** The strategy to use in phrase control. */
  strategy: PhraseControlStrategy;
}

/** An object representing the summarization results of each document. */
export interface AbstractiveSummarizationResultBase {
  /** Response by document */
  documents: AbstractiveSummarizationResultBaseDocumentsItem[];
}

/** An object representing a single summary with context for given document. */
export interface AbstractiveSummary {
  /** The text of the summary. */
  text: string;
  /** The context list of the summary. */
  contexts?: SummaryContext[];
}

/** The context of the summary. */
export interface SummaryContext {
  /** Start position for the context. Use of different 'stringIndexType' values can affect the offset returned. */
  offset: number;
  /** The length of the context. Use of different 'stringIndexType' values can affect the length returned. */
  length: number;
}

/** Represents resolutions for quantities. */
export interface QuantityResolution {
  /** The numeric value that the extracted text denotes. */
  value: number;
}

export interface AnalyzeTextEntityLinkingInput extends AnalyzeAction {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "EntityLinking";
  analysisInput?: MultiLanguageAnalysisInput;
  /** Options for an entity linking action. */
  parameters?: EntityLinkingAction;
}

export interface AnalyzeTextEntityRecognitionInput extends AnalyzeAction {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "EntityRecognition";
  analysisInput?: MultiLanguageAnalysisInput;
  /** Options for an entity recognition action. */
  parameters?: EntityRecognitionAction;
}

export interface AnalyzeTextKeyPhraseExtractionInput extends AnalyzeAction {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "KeyPhraseExtraction";
  analysisInput?: MultiLanguageAnalysisInput;
  /** Options for a key phrase recognition action. */
  parameters?: KeyPhraseExtractionAction;
}

export interface AnalyzeTextPiiEntitiesRecognitionInput extends AnalyzeAction {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "PiiEntityRecognition";
  analysisInput?: MultiLanguageAnalysisInput;
  /** Options for a Pii entity recognition action. */
  parameters?: PiiEntityRecognitionAction;
}

export interface AnalyzeTextLanguageDetectionInput extends AnalyzeAction {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "LanguageDetection";
  analysisInput?: LanguageDetectionAnalysisInput;
  /** Options for a language detection action. */
  parameters?: LanguageDetectionAction;
}

export interface AnalyzeTextSentimentAnalysisInput extends AnalyzeAction {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "SentimentAnalysis";
  analysisInput?: MultiLanguageAnalysisInput;
  /** Options for a sentiment analysis action. */
  parameters?: SentimentAnalysisAction;
}

export interface AnalyzeTextDynamicClassificationInput extends AnalyzeAction {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "DynamicClassification";
  analysisInput?: MultiLanguageAnalysisInput;
  /** Options for a dynamic classification action. */
  parameters?: DynamicClassificationAction;
}

export interface SentimentTaskResult extends AnalyzeTextTaskResult {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "SentimentAnalysisResults";
  results: SentimentResponse;
}

export interface EntitiesTaskResult extends AnalyzeTextTaskResult {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "EntityRecognitionResults";
  results: EntitiesResult;
}

export interface EntityLinkingTaskResult extends AnalyzeTextTaskResult {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "EntityLinkingResults";
  results: EntityLinkingResult;
}

export interface PiiTaskResult extends AnalyzeTextTaskResult {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "PiiEntityRecognitionResults";
  results: PiiResult;
}

export interface KeyPhraseTaskResult extends AnalyzeTextTaskResult {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "KeyPhraseExtractionResults";
  results: KeyPhraseResult;
}

export interface LanguageDetectionTaskResult extends AnalyzeTextTaskResult {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "LanguageDetectionResults";
  results: LanguageDetectionResult;
}

export interface DynamicClassificationTaskResult extends AnalyzeTextTaskResult {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "DynamicClassificationResults";
  results: DynamicClassificationResult;
}

export interface AnalyzeBatchAction extends BatchActionState {
  /** Enumeration of supported long-running Text Analysis tasks. */
  kind: AnalyzeTextLROTaskKind;
}

export interface AnalyzeTextLROResult extends TaskState, BatchActionState {
  /** Enumeration of supported Text Analysis long-running operation task results. */
  kind: AnalyzeTextLROResultsKind;
}

export interface AnalyzeTextJobState
  extends JobState,
    TasksState,
    AnalyzeTextJobStatistics {}

/** if includeStatistics=true was specified in the request this field will contain information about the document request payload. */
export interface DocumentRequestStatistics extends TextDocumentBatchStatistics {
  /** Number of documents submitted in the request. */
  documentsCount: number;
  /** Number of valid documents. This excludes empty, over-size limit or non-supported languages documents. */
  validDocumentsCount: number;
  /** Number of invalid documents. This includes empty, over-size limit or non-supported languages documents. */
  erroneousDocumentsCount: number;
}

/** Configuration common to all actions that use prebuilt models. */
export interface ActionPrebuilt extends ActionCommon {
  /** The version of the model to be used by the action. */
  modelVersion?: string;
}

/** Configuration common to all actions that use custom models. */
export interface ActionCustom extends ActionCommon {
  /** The project name for the model to be used by the action. */
  projectName: string;
  /** The deployment name for the model to be used by the action. */
  deploymentName: string;
}

export interface CustomEntitiesResult extends CustomResult {
  /** Response by document */
  documents: CustomEntitiesResultDocumentsItem[];
}

export interface CustomLabelClassificationResult extends CustomResult {
  /** Response by document */
  documents: CustomLabelClassificationResultDocumentsItem[];
}

/** Represents the Age entity resolution model. */
export interface AgeResolution extends BaseResolution, QuantityResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "AgeResolution";
  /** The Age Unit of measurement */
  unit: AgeUnit;
}

/** Represents the volume entity resolution model. */
export interface VolumeResolution extends BaseResolution, QuantityResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "VolumeResolution";
  /** The Volume Unit of measurement */
  unit: VolumeUnit;
}

/** Represents the speed entity resolution model. */
export interface SpeedResolution extends BaseResolution, QuantityResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "SpeedResolution";
  /** The speed Unit of measurement */
  unit: SpeedUnit;
}

/** Represents the area entity resolution model. */
export interface AreaResolution extends BaseResolution, QuantityResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "AreaResolution";
  /** The area Unit of measurement */
  unit: AreaUnit;
}

/** Represents the length entity resolution model. */
export interface LengthResolution extends BaseResolution, QuantityResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "LengthResolution";
  /** The length Unit of measurement */
  unit: LengthUnit;
}

/** Represents the information (data) entity resolution model. */
export interface InformationResolution
  extends BaseResolution,
    QuantityResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "InformationResolution";
  /** The information (data) Unit of measurement. */
  unit: InformationUnit;
}

/** Represents the temperature entity resolution model. */
export interface TemperatureResolution
  extends BaseResolution,
    QuantityResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "TemperatureResolution";
  /** The temperature Unit of measurement. */
  unit: TemperatureUnit;
}

/** Represents the weight entity resolution model. */
export interface WeightResolution extends BaseResolution, QuantityResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "WeightResolution";
  /** The weight Unit of measurement. */
  unit: WeightUnit;
}

/** Represents the currency entity resolution model. */
export interface CurrencyResolution extends BaseResolution, QuantityResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "CurrencyResolution";
  /** The alphabetic code based on another ISO standard, ISO 3166, which lists the codes for country names. The first two letters of the ISO 4217 three-letter code are the same as the code for the country name, and, where possible, the third letter corresponds to the first letter of the currency name. */
  iso4217?: string;
  /** The unit of the amount captured in the extracted entity */
  unit: string;
}

/** A resolution for boolean expressions */
export interface BooleanResolution extends BaseResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "BooleanResolution";
  value: boolean;
}

/** A resolution for datetime entity instances. */
export interface DateTimeResolution extends BaseResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "DateTimeResolution";
  /** An extended ISO 8601 date/time representation as described in (https://github.com/Microsoft/Recognizers-Text/blob/master/Patterns/English/English-DateTime.yaml) */
  timex: string;
  /** The DateTime SubKind */
  dateTimeSubKind: DateTimeSubKind;
  /** The actual time that the extracted text denote. */
  value: string;
  /** An optional modifier of a date/time instance. */
  modifier?: TemporalModifier;
}

/** A resolution for numeric entity instances. */
export interface NumberResolution extends BaseResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "NumberResolution";
  /** The type of the extracted number entity. */
  numberKind: NumberKind;
  /** A numeric representation of what the extracted text denotes. */
  value: string;
}

/** A resolution for ordinal numbers entity instances. */
export interface OrdinalResolution extends BaseResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "OrdinalResolution";
  /** The offset With respect to the reference (e.g., offset = -1 in "show me the second to last" */
  offset: string;
  /** The reference point that the ordinal number denotes. */
  relativeTo: RelativeTo;
  /** A simple arithmetic expression that the ordinal denotes. */
  value: string;
}

/** represents the resolution of a date and/or time span. */
export interface TemporalSpanResolution extends BaseResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "TemporalSpanResolution";
  /** An extended ISO 8601 date/time representation as described in (https://github.com/Microsoft/Recognizers-Text/blob/master/Patterns/English/English-DateTime.yaml) */
  begin?: string;
  /** An extended ISO 8601 date/time representation as described in (https://github.com/Microsoft/Recognizers-Text/blob/master/Patterns/English/English-DateTime.yaml) */
  end?: string;
  /** An optional duration value formatted based on the ISO 8601 (https://en.wikipedia.org/wiki/ISO_8601#Durations) */
  duration?: string;
  /** An optional modifier of a date/time instance. */
  modifier?: TemporalModifier;
}

/** represents the resolution of numeric intervals. */
export interface NumericRangeResolution extends BaseResolution {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  resolutionKind: "NumericRangeResolution";
  /** The kind of range that the resolution object represents. */
  rangeKind: RangeKind;
  /** The beginning value of  the interval. */
  minimum: number;
  /** The ending value of the interval. */
  maximum: number;
}

/** An entity with resolution. */
export interface EntityWithResolution extends Entity {
  /** The collection of entity resolution objects. */
  resolutions?: BaseResolutionUnion[];
}

export interface EntitiesDocumentResult extends DocumentResult {
  /** Recognized entities in the document. */
  entities: EntityWithResolution[];
}

export interface ClassificationDocumentResult extends DocumentResult {
  class: ClassificationCategory[];
}

export interface HealthcareEntitiesDocumentResult extends DocumentResult {
  /** Healthcare entities. */
  entities: HealthcareEntity[];
  /** Healthcare entity relations. */
  relations: HealthcareRelation[];
  /** JSON bundle containing a FHIR compatible object for consumption in other Healthcare tools. For additional information see https://www.hl7.org/fhir/overview.html. */
  fhirBundle?: { [propertyName: string]: any };
}

export interface SentimentDocumentResult extends DocumentResult {
  /** Predicted sentiment for document (Negative, Neutral, Positive, or Mixed). */
  sentiment: DocumentSentimentLabel;
  /** Document level sentiment confidence scores between 0 and 1 for each sentiment class. */
  confidenceScores: SentimentConfidenceScores;
  /** Sentence level sentiment analysis. */
  sentences: SentenceSentiment[];
}

export interface LinkedEntitiesDocumentResult extends DocumentResult {
  /** Recognized well known entities in the document. */
  entities: LinkedEntity[];
}

export interface PiiEntitiesDocumentResult extends DocumentResult {
  /** Returns redacted text. */
  redactedText: string;
  /** Recognized entities in the document. */
  entities: Entity[];
}

export interface ExtractedSummaryDocumentResult extends DocumentResult {
  /** A ranked list of sentences representing the extracted summary. */
  sentences: SummarySentence[];
}

export interface KeyPhrasesDocumentResult extends DocumentResult {
  /** A list of representative words or phrases. The number of key phrases returned is proportional to the number of words in the input document. */
  keyPhrases: string[];
}

export interface LanguageDetectionDocumentResult extends DocumentResult {
  /** Detected Language. */
  detectedLanguage: DetectedLanguage;
}

/** An object representing the summarization result of a single document. */
export interface AbstractiveSummaryDocumentResult extends DocumentResult {
  /** A list of abstractive summaries. */
  summaries: AbstractiveSummary[];
}

export interface CustomEntitiesResultDocumentsItem
  extends EntitiesDocumentResult,
    DocumentDetectedLanguage {}

export interface CustomLabelClassificationResultDocumentsItem
  extends ClassificationDocumentResult,
    DocumentDetectedLanguage {}

export interface HealthcareResultDocumentsItem
  extends HealthcareEntitiesDocumentResult,
    DocumentDetectedLanguage {}

export interface SentimentResponseDocumentsItem
  extends SentimentDocumentResult,
    DocumentDetectedLanguage {}

export interface EntitiesResultDocumentsItem
  extends EntitiesDocumentResult,
    DocumentDetectedLanguage {}

export interface EntityLinkingResultDocumentsItem
  extends LinkedEntitiesDocumentResult,
    DocumentDetectedLanguage {}

export interface PiiResultDocumentsItem
  extends PiiEntitiesDocumentResult,
    DocumentDetectedLanguage {}

export interface ExtractiveSummarizationResultDocumentsItem
  extends ExtractedSummaryDocumentResult,
    DocumentDetectedLanguage {}

export interface KeyPhraseResultDocumentsItem
  extends KeyPhrasesDocumentResult,
    DocumentDetectedLanguage {}

export interface AbstractiveSummarizationResultBaseDocumentsItem
  extends AbstractiveSummaryDocumentResult,
    DocumentDetectedLanguage {}

export interface HealthcareResult extends PreBuiltResult {
  documents: HealthcareResultDocumentsItem[];
}

export interface SentimentResponse extends PreBuiltResult {
  /** Sentiment analysis per document. */
  documents: SentimentResponseDocumentsItem[];
}

export interface EntitiesResult extends PreBuiltResult {
  /** Response by document */
  documents: EntitiesResultDocumentsItem[];
}

export interface EntityLinkingResult extends PreBuiltResult {
  /** Response by document */
  documents: EntityLinkingResultDocumentsItem[];
}

export interface PiiResult extends PreBuiltResult {
  /** Response by document */
  documents: PiiResultDocumentsItem[];
}

export interface ExtractiveSummarizationResult extends PreBuiltResult {
  /** Response by document */
  documents: ExtractiveSummarizationResultDocumentsItem[];
}

export interface KeyPhraseResult extends PreBuiltResult {
  /** Response by document */
  documents: KeyPhraseResultDocumentsItem[];
}

export interface LanguageDetectionResult extends PreBuiltResult {
  /** Response by document */
  documents: LanguageDetectionDocumentResult[];
}

export interface DynamicClassificationResult extends PreBuiltResult {
  /** Response by document */
  documents: DynamicClassificationResultDocumentsItem[];
}

/** An object representing the pre-build summarization results of each document. */
export interface AbstractiveSummarizationResult
  extends AbstractiveSummarizationResultBase,
    PreBuiltResult {}

/** Supported parameters for the pre-build Abstractive Summarization task. */
export interface AbstractiveSummarizationAction
  extends AbstractiveSummarizationTaskParametersBase,
    ActionPrebuilt {}

/** Use custom models to ease the process of information extraction from unstructured documents like contracts or financial documents */
export interface CustomEntitiesLROTask extends AnalyzeBatchAction {
  /** Supported parameters for a Custom Entities task. */
  parameters?: CustomEntityRecognitionAction;
}

/** Use custom models to classify text into single label taxonomy */
export interface CustomSingleLabelClassificationLROTask
  extends AnalyzeBatchAction {
  /** Options for a single-label classification custom action */
  parameters?: CustomSingleLabelClassificationAction;
}

/** Use custom models to classify text into multi label taxonomy */
export interface CustomMultiLabelClassificationLROTask
  extends AnalyzeBatchAction {
  /** Options for a multi-label classification custom action */
  parameters?: CustomMultiLabelClassificationAction;
}

export interface HealthcareLROTask extends AnalyzeBatchAction {
  /** Supported parameters for a Healthcare task. */
  parameters?: HealthcareAction;
}

/** An object representing the task definition for a Sentiment Analysis task. */
export interface SentimentAnalysisLROTask extends AnalyzeBatchAction {
  /** Options for a sentiment analysis action. */
  parameters?: SentimentAnalysisAction;
}

/** An object representing the task definition for an Entities Recognition task. */
export interface EntitiesLROTask extends AnalyzeBatchAction {
  /** Options for an entity recognition action. */
  parameters?: EntityRecognitionAction;
}

/** An object representing the task definition for an Entity Linking task. */
export interface EntityLinkingLROTask extends AnalyzeBatchAction {
  /** Options for an entity linking action. */
  parameters?: EntityLinkingAction;
}

/** An object representing the task definition for a PII Entities Recognition task. */
export interface PiiLROTask extends AnalyzeBatchAction {
  /** Options for a Pii entity recognition action. */
  parameters?: PiiEntityRecognitionAction;
}

/** An object representing the task definition for an Extractive Summarization task. */
export interface ExtractiveSummarizationLROTask extends AnalyzeBatchAction {
  /** Supported parameters for an Extractive Summarization task. */
  parameters?: ExtractiveSummarizationAction;
}

/** An object representing the task definition for a Key Phrase Extraction task. */
export interface KeyPhraseLROTask extends AnalyzeBatchAction {
  /** Options for a key phrase recognition action. */
  parameters?: KeyPhraseExtractionAction;
}

/** An object representing the task definition for an Abstractive Summarization task. */
export interface AbstractiveSummarizationLROTask extends AnalyzeBatchAction {
  /** Supported parameters for the pre-build Abstractive Summarization task. */
  parameters: AbstractiveSummarizationAction;
}

export interface EntityRecognitionLROResult extends AnalyzeTextLROResult {
  results: EntitiesResult;
}

export interface CustomEntityRecognitionLROResult extends AnalyzeTextLROResult {
  results: CustomEntitiesResult;
}

export interface CustomSingleLabelClassificationLROResult
  extends AnalyzeTextLROResult {
  results: CustomLabelClassificationResult;
}

export interface CustomMultiLabelClassificationLROResult
  extends AnalyzeTextLROResult {
  results: CustomLabelClassificationResult;
}

export interface EntityLinkingLROResult extends AnalyzeTextLROResult {
  results: EntityLinkingResult;
}

export interface PiiEntityRecognitionLROResult extends AnalyzeTextLROResult {
  results: PiiResult;
}

export interface ExtractiveSummarizationLROResult extends AnalyzeTextLROResult {
  results: ExtractiveSummarizationResult;
}

export interface HealthcareLROResult extends AnalyzeTextLROResult {
  results: HealthcareResult;
}

export interface SentimentLROResult extends AnalyzeTextLROResult {
  results: SentimentResponse;
}

export interface KeyPhraseExtractionLROResult extends AnalyzeTextLROResult {
  results: KeyPhraseResult;
}

/** An object representing the results for an Abstractive Summarization task. */
export interface AbstractiveSummarizationLROResult
  extends AnalyzeTextLROResult {
  /** An object representing the pre-build summarization results of each document. */
  results: AbstractiveSummarizationResult;
}

/** Options for an entity linking action. */
export interface EntityLinkingAction extends ActionPrebuilt {
  /**
   * Specifies the measurement unit used to calculate the offset and length properties. For a list of possible values, see {@link KnownStringIndexType}.
   *
   * The default is the JavaScript's default which is "Utf16CodeUnit".
   */
  stringIndexType?: StringIndexType;
}

/** Options for an entity recognition action. */
export interface EntityRecognitionAction extends ActionPrebuilt {
  /**
   * Specifies the measurement unit used to calculate the offset and length properties. For a list of possible values, see {@link KnownStringIndexType}.
   *
   * The default is the JavaScript's default which is "Utf16CodeUnit".
   */
  stringIndexType?: StringIndexType;
}

/** Options for a key phrase recognition action. */
export interface KeyPhraseExtractionAction extends ActionPrebuilt {}

/** Options for a Pii entity recognition action. */
export interface PiiEntityRecognitionAction extends ActionPrebuilt {
  /**
   * Filters entities to ones only included in the specified domain (e.g., if set to `Phi`, only entities in the Protected Healthcare Information domain will be returned). For a list of possible domains, see {@link KnownPiiEntityDomain}.
   *
   * See {@link https://aka.ms/tanerpii the service documentation} for more information.
   */
  domainFilter?: PiiEntityDomain;
  /** Filters entities to ones only included in the specified array of categories. For a list of possible categories, see {@link KnownPiiCategory} */
  categoriesFilter?: PiiEntityCategory[];
  /**
   * Specifies the measurement unit used to calculate the offset and length properties. For a list of possible values, see {@link KnownStringIndexType}.
   *
   * The default is the JavaScript's default which is "Utf16CodeUnit".
   */
  stringIndexType?: StringIndexType;
}

/** Options for a language detection action. */
export interface LanguageDetectionAction extends ActionPrebuilt {}

/** Options for a sentiment analysis action. */
export interface SentimentAnalysisAction extends ActionPrebuilt {
  /** Enables performing opinion mining on the input documents, a more  granular analysis around the aspects of a product or service (also known as aspect-based sentiment analysis). If set to true, {@link SentenceSentiment.opinions} will contain the results of this analysis. See {@link https://docs.microsoft.com/azure/cognitive-services/language-service/sentiment-opinion-mining/overview#opinion-mining the service documentation} for more information. */
  includeOpinionMining?: boolean;
  /**
   * Specifies the measurement unit used to calculate the offset and length properties. For a list of possible values, see {@link KnownStringIndexType}.
   *
   * The default is the JavaScript's default which is "Utf16CodeUnit".
   */
  stringIndexType?: StringIndexType;
}

/** Options for a dynamic classification action. */
export interface DynamicClassificationAction extends ActionPrebuilt {
  /** Specifies either one or multiple categories per document. Defaults to multi classification which may return more than one class for each document. */
  classificationType?: ClassificationType;
  /** a list of categories to which input is classified to. */
  categories: string[];
}

/** Supported parameters for a Healthcare task. */
export interface HealthcareAction extends ActionPrebuilt {
  /** The FHIR Spec version that the result will use to format the fhirBundle. For additional information see https://www.hl7.org/fhir/overview.html. */
  fhirVersion?: FhirVersion;
  /** Document type that can be provided as input for Fhir Documents. Expect to have fhirVersion provided when used. Behavior of using None enum is the same as not using the documentType parameter. */
  documentType?: DocumentType;
  /**
   * Specifies the measurement unit used to calculate the offset and length properties. For a list of possible values, see {@link KnownStringIndexType}.
   *
   * The default is the JavaScript's default which is "Utf16CodeUnit".
   */
  stringIndexType?: StringIndexType;
}

/** Supported parameters for an Extractive Summarization task. */
export interface ExtractiveSummarizationAction extends ActionPrebuilt {
  /** The max number of sentences to be part of the summary. */
  maxSentenceCount?: number;
  /** The sorting criteria to use for the results of Extractive Summarization. */
  orderBy?: ExtractiveSummarizationOrderingCriteria;
  /**
   * Specifies the measurement unit used to calculate the offset and length properties. For a list of possible values, see {@link KnownStringIndexType}.
   *
   * The default is the JavaScript's default which is "Utf16CodeUnit".
   */
  stringIndexType?: StringIndexType;
}

/** Supported parameters for a Custom Entities task. */
export interface CustomEntityRecognitionAction extends ActionCustom {
  /**
   * Specifies the measurement unit used to calculate the offset and length properties. For a list of possible values, see {@link KnownStringIndexType}.
   *
   * The default is the JavaScript's default which is "Utf16CodeUnit".
   */
  stringIndexType?: StringIndexType;
}

/** Options for a single-label classification custom action */
export interface CustomSingleLabelClassificationAction extends ActionCustom {}

/** Options for a multi-label classification custom action */
export interface CustomMultiLabelClassificationAction extends ActionCustom {}

export interface DynamicClassificationResultDocumentsItem
  extends ClassificationDocumentResult {}

/** Defines headers for GeneratedClient_analyzeBatch operation. */
export interface GeneratedClientAnalyzeBatchHeaders {
  operationLocation?: string;
}

/** Defines headers for AnalyzeText_cancelJob operation. */
export interface AnalyzeTextCancelJobHeaders {
  operationLocation?: string;
}

/** Known values of {@link AnalyzeTextTaskKind} that the service accepts. */
export enum KnownAnalyzeTextTaskKind {
  /** SentimentAnalysis */
  SentimentAnalysis = "SentimentAnalysis",
  /** EntityRecognition */
  EntityRecognition = "EntityRecognition",
  /** PiiEntityRecognition */
  PiiEntityRecognition = "PiiEntityRecognition",
  /** KeyPhraseExtraction */
  KeyPhraseExtraction = "KeyPhraseExtraction",
  /** LanguageDetection */
  LanguageDetection = "LanguageDetection",
  /** EntityLinking */
  EntityLinking = "EntityLinking",
  /** DynamicClassification */
  DynamicClassification = "DynamicClassification"
}

/**
 * Defines values for AnalyzeTextTaskKind. \
 * {@link KnownAnalyzeTextTaskKind} can be used interchangeably with AnalyzeTextTaskKind,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **SentimentAnalysis** \
 * **EntityRecognition** \
 * **PiiEntityRecognition** \
 * **KeyPhraseExtraction** \
 * **LanguageDetection** \
 * **EntityLinking** \
 * **DynamicClassification**
 */
export type AnalyzeTextTaskKind = string;

/** Known values of {@link AnalyzeTextTaskResultsKind} that the service accepts. */
export enum KnownAnalyzeTextTaskResultsKind {
  /** SentimentAnalysisResults */
  SentimentAnalysisResults = "SentimentAnalysisResults",
  /** EntityRecognitionResults */
  EntityRecognitionResults = "EntityRecognitionResults",
  /** PiiEntityRecognitionResults */
  PiiEntityRecognitionResults = "PiiEntityRecognitionResults",
  /** KeyPhraseExtractionResults */
  KeyPhraseExtractionResults = "KeyPhraseExtractionResults",
  /** LanguageDetectionResults */
  LanguageDetectionResults = "LanguageDetectionResults",
  /** EntityLinkingResults */
  EntityLinkingResults = "EntityLinkingResults",
  /** DynamicClassificationResults */
  DynamicClassificationResults = "DynamicClassificationResults"
}

/**
 * Defines values for AnalyzeTextTaskResultsKind. \
 * {@link KnownAnalyzeTextTaskResultsKind} can be used interchangeably with AnalyzeTextTaskResultsKind,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **SentimentAnalysisResults** \
 * **EntityRecognitionResults** \
 * **PiiEntityRecognitionResults** \
 * **KeyPhraseExtractionResults** \
 * **LanguageDetectionResults** \
 * **EntityLinkingResults** \
 * **DynamicClassificationResults**
 */
export type AnalyzeTextTaskResultsKind = string;

/** Known values of {@link ErrorCode} that the service accepts. */
export enum KnownErrorCode {
  /** InvalidRequest */
  InvalidRequest = "InvalidRequest",
  /** InvalidArgument */
  InvalidArgument = "InvalidArgument",
  /** Unauthorized */
  Unauthorized = "Unauthorized",
  /** Forbidden */
  Forbidden = "Forbidden",
  /** NotFound */
  NotFound = "NotFound",
  /** ProjectNotFound */
  ProjectNotFound = "ProjectNotFound",
  /** OperationNotFound */
  OperationNotFound = "OperationNotFound",
  /** AzureCognitiveSearchNotFound */
  AzureCognitiveSearchNotFound = "AzureCognitiveSearchNotFound",
  /** AzureCognitiveSearchIndexNotFound */
  AzureCognitiveSearchIndexNotFound = "AzureCognitiveSearchIndexNotFound",
  /** TooManyRequests */
  TooManyRequests = "TooManyRequests",
  /** AzureCognitiveSearchThrottling */
  AzureCognitiveSearchThrottling = "AzureCognitiveSearchThrottling",
  /** AzureCognitiveSearchIndexLimitReached */
  AzureCognitiveSearchIndexLimitReached = "AzureCognitiveSearchIndexLimitReached",
  /** InternalServerError */
  InternalServerError = "InternalServerError",
  /** ServiceUnavailable */
  ServiceUnavailable = "ServiceUnavailable",
  /** Timeout */
  Timeout = "Timeout",
  /** QuotaExceeded */
  QuotaExceeded = "QuotaExceeded",
  /** Conflict */
  Conflict = "Conflict",
  /** Warning */
  Warning = "Warning"
}

/**
 * Defines values for ErrorCode. \
 * {@link KnownErrorCode} can be used interchangeably with ErrorCode,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **InvalidRequest** \
 * **InvalidArgument** \
 * **Unauthorized** \
 * **Forbidden** \
 * **NotFound** \
 * **ProjectNotFound** \
 * **OperationNotFound** \
 * **AzureCognitiveSearchNotFound** \
 * **AzureCognitiveSearchIndexNotFound** \
 * **TooManyRequests** \
 * **AzureCognitiveSearchThrottling** \
 * **AzureCognitiveSearchIndexLimitReached** \
 * **InternalServerError** \
 * **ServiceUnavailable** \
 * **Timeout** \
 * **QuotaExceeded** \
 * **Conflict** \
 * **Warning**
 */
export type ErrorCode = string;

/** Known values of {@link InnerErrorCode} that the service accepts. */
export enum KnownInnerErrorCode {
  /** InvalidRequest */
  InvalidRequest = "InvalidRequest",
  /** InvalidParameterValue */
  InvalidParameterValue = "InvalidParameterValue",
  /** KnowledgeBaseNotFound */
  KnowledgeBaseNotFound = "KnowledgeBaseNotFound",
  /** AzureCognitiveSearchNotFound */
  AzureCognitiveSearchNotFound = "AzureCognitiveSearchNotFound",
  /** AzureCognitiveSearchThrottling */
  AzureCognitiveSearchThrottling = "AzureCognitiveSearchThrottling",
  /** ExtractionFailure */
  ExtractionFailure = "ExtractionFailure",
  /** InvalidRequestBodyFormat */
  InvalidRequestBodyFormat = "InvalidRequestBodyFormat",
  /** EmptyRequest */
  EmptyRequest = "EmptyRequest",
  /** MissingInputDocuments */
  MissingInputDocuments = "MissingInputDocuments",
  /** InvalidDocument */
  InvalidDocument = "InvalidDocument",
  /** ModelVersionIncorrect */
  ModelVersionIncorrect = "ModelVersionIncorrect",
  /** InvalidDocumentBatch */
  InvalidDocumentBatch = "InvalidDocumentBatch",
  /** UnsupportedLanguageCode */
  UnsupportedLanguageCode = "UnsupportedLanguageCode",
  /** InvalidCountryHint */
  InvalidCountryHint = "InvalidCountryHint"
}

/**
 * Defines values for InnerErrorCode. \
 * {@link KnownInnerErrorCode} can be used interchangeably with InnerErrorCode,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **InvalidRequest** \
 * **InvalidParameterValue** \
 * **KnowledgeBaseNotFound** \
 * **AzureCognitiveSearchNotFound** \
 * **AzureCognitiveSearchThrottling** \
 * **ExtractionFailure** \
 * **InvalidRequestBodyFormat** \
 * **EmptyRequest** \
 * **MissingInputDocuments** \
 * **InvalidDocument** \
 * **ModelVersionIncorrect** \
 * **InvalidDocumentBatch** \
 * **UnsupportedLanguageCode** \
 * **InvalidCountryHint**
 */
export type InnerErrorCode = string;

/** Known values of {@link AnalyzeTextLROTaskKind} that the service accepts. */
export enum KnownAnalyzeTextLROTaskKind {
  /** SentimentAnalysis */
  SentimentAnalysis = "SentimentAnalysis",
  /** EntityRecognition */
  EntityRecognition = "EntityRecognition",
  /** PiiEntityRecognition */
  PiiEntityRecognition = "PiiEntityRecognition",
  /** KeyPhraseExtraction */
  KeyPhraseExtraction = "KeyPhraseExtraction",
  /** EntityLinking */
  EntityLinking = "EntityLinking",
  /** Healthcare */
  Healthcare = "Healthcare",
  /** ExtractiveSummarization */
  ExtractiveSummarization = "ExtractiveSummarization",
  /** CustomEntityRecognition */
  CustomEntityRecognition = "CustomEntityRecognition",
  /** CustomSingleLabelClassification */
  CustomSingleLabelClassification = "CustomSingleLabelClassification",
  /** CustomMultiLabelClassification */
  CustomMultiLabelClassification = "CustomMultiLabelClassification",
  /** AbstractiveSummarization */
  AbstractiveSummarization = "AbstractiveSummarization"
}

/**
 * Defines values for AnalyzeTextLROTaskKind. \
 * {@link KnownAnalyzeTextLROTaskKind} can be used interchangeably with AnalyzeTextLROTaskKind,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **SentimentAnalysis** \
 * **EntityRecognition** \
 * **PiiEntityRecognition** \
 * **KeyPhraseExtraction** \
 * **EntityLinking** \
 * **Healthcare** \
 * **ExtractiveSummarization** \
 * **CustomEntityRecognition** \
 * **CustomSingleLabelClassification** \
 * **CustomMultiLabelClassification** \
 * **AbstractiveSummarization**
 */
export type AnalyzeTextLROTaskKind = string;

/** Known values of {@link OperationStatus} that the service accepts. */
export enum KnownOperationStatus {
  /** NotStarted */
  NotStarted = "notStarted",
  /** Running */
  Running = "running",
  /** Succeeded */
  Succeeded = "succeeded",
  /** PartiallyCompleted */
  PartiallyCompleted = "partiallyCompleted",
  /** Failed */
  Failed = "failed",
  /** Cancelled */
  Cancelled = "cancelled",
  /** Cancelling */
  Cancelling = "cancelling"
}

/**
 * Defines values for OperationStatus. \
 * {@link KnownOperationStatus} can be used interchangeably with OperationStatus,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **notStarted** \
 * **running** \
 * **succeeded** \
 * **partiallyCompleted** \
 * **failed** \
 * **cancelled** \
 * **cancelling**
 */
export type OperationStatus = string;

/** Known values of {@link AnalyzeTextLROResultsKind} that the service accepts. */
export enum KnownAnalyzeTextLROResultsKind {
  /** SentimentAnalysisLROResults */
  SentimentAnalysisLROResults = "SentimentAnalysisLROResults",
  /** EntityRecognitionLROResults */
  EntityRecognitionLROResults = "EntityRecognitionLROResults",
  /** PiiEntityRecognitionLROResults */
  PiiEntityRecognitionLROResults = "PiiEntityRecognitionLROResults",
  /** KeyPhraseExtractionLROResults */
  KeyPhraseExtractionLROResults = "KeyPhraseExtractionLROResults",
  /** EntityLinkingLROResults */
  EntityLinkingLROResults = "EntityLinkingLROResults",
  /** HealthcareLROResults */
  HealthcareLROResults = "HealthcareLROResults",
  /** ExtractiveSummarizationLROResults */
  ExtractiveSummarizationLROResults = "ExtractiveSummarizationLROResults",
  /** CustomEntityRecognitionLROResults */
  CustomEntityRecognitionLROResults = "CustomEntityRecognitionLROResults",
  /** CustomSingleLabelClassificationLROResults */
  CustomSingleLabelClassificationLROResults = "CustomSingleLabelClassificationLROResults",
  /** CustomMultiLabelClassificationLROResults */
  CustomMultiLabelClassificationLROResults = "CustomMultiLabelClassificationLROResults",
  /** AbstractiveSummarizationLROResults */
  AbstractiveSummarizationLROResults = "AbstractiveSummarizationLROResults"
}

/**
 * Defines values for AnalyzeTextLROResultsKind. \
 * {@link KnownAnalyzeTextLROResultsKind} can be used interchangeably with AnalyzeTextLROResultsKind,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **SentimentAnalysisLROResults** \
 * **EntityRecognitionLROResults** \
 * **PiiEntityRecognitionLROResults** \
 * **KeyPhraseExtractionLROResults** \
 * **EntityLinkingLROResults** \
 * **HealthcareLROResults** \
 * **ExtractiveSummarizationLROResults** \
 * **CustomEntityRecognitionLROResults** \
 * **CustomSingleLabelClassificationLROResults** \
 * **CustomMultiLabelClassificationLROResults** \
 * **AbstractiveSummarizationLROResults**
 */
export type AnalyzeTextLROResultsKind = string;

/** Known values of {@link State} that the service accepts. */
export enum KnownState {
  /** NotStarted */
  NotStarted = "notStarted",
  /** Running */
  Running = "running",
  /** Succeeded */
  Succeeded = "succeeded",
  /** Failed */
  Failed = "failed",
  /** Cancelled */
  Cancelled = "cancelled",
  /** Cancelling */
  Cancelling = "cancelling"
}

/**
 * Defines values for State. \
 * {@link KnownState} can be used interchangeably with State,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **notStarted** \
 * **running** \
 * **succeeded** \
 * **failed** \
 * **cancelled** \
 * **cancelling**
 */
export type State = string;

/** Known values of {@link StringIndexType} that the service accepts. */
export enum KnownStringIndexType {
  /** Returned offset and length values will correspond to TextElements (Graphemes and Grapheme clusters) confirming to the Unicode 8.0.0 standard. Use this option if your application is written in .Net Framework or .Net Core and you will be using StringInfo. */
  TextElementsV8 = "TextElements_v8",
  /** Returned offset and length values will correspond to Unicode code points. Use this option if your application is written in a language that support Unicode, for example Python. */
  UnicodeCodePoint = "UnicodeCodePoint",
  /** Returned offset and length values will correspond to UTF-16 code units. Use this option if your application is written in a language that support Unicode, for example Java, JavaScript. */
  Utf16CodeUnit = "Utf16CodeUnit"
}

/**
 * Defines values for StringIndexType. \
 * {@link KnownStringIndexType} can be used interchangeably with StringIndexType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **TextElements_v8**: Returned offset and length values will correspond to TextElements (Graphemes and Grapheme clusters) confirming to the Unicode 8.0.0 standard. Use this option if your application is written in .Net Framework or .Net Core and you will be using StringInfo. \
 * **UnicodeCodePoint**: Returned offset and length values will correspond to Unicode code points. Use this option if your application is written in a language that support Unicode, for example Python. \
 * **Utf16CodeUnit**: Returned offset and length values will correspond to UTF-16 code units. Use this option if your application is written in a language that support Unicode, for example Java, JavaScript.
 */
export type StringIndexType = string;

/** Known values of {@link PiiEntityDomain} that the service accepts. */
export enum KnownPiiEntityDomain {
  /** Indicates that entities in the Personal Health Information domain should be redacted. */
  Phi = "phi",
  /** Indicates that no domain is specified. */
  None = "none"
}

/**
 * Defines values for PiiEntityDomain. \
 * {@link KnownPiiEntityDomain} can be used interchangeably with PiiEntityDomain,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **phi**: Indicates that entities in the Personal Health Information domain should be redacted. \
 * **none**: Indicates that no domain is specified.
 */
export type PiiEntityDomain = string;

/** Known values of {@link PiiEntityCategory} that the service accepts. */
export enum KnownPiiEntityCategory {
  /** ABARoutingNumber */
  ABARoutingNumber = "ABARoutingNumber",
  /** ARNationalIdentityNumber */
  ARNationalIdentityNumber = "ARNationalIdentityNumber",
  /** AUBankAccountNumber */
  AUBankAccountNumber = "AUBankAccountNumber",
  /** AUDriversLicenseNumber */
  AUDriversLicenseNumber = "AUDriversLicenseNumber",
  /** AUMedicalAccountNumber */
  AUMedicalAccountNumber = "AUMedicalAccountNumber",
  /** AUPassportNumber */
  AUPassportNumber = "AUPassportNumber",
  /** AUTaxFileNumber */
  AUTaxFileNumber = "AUTaxFileNumber",
  /** AUBusinessNumber */
  AUBusinessNumber = "AUBusinessNumber",
  /** AUCompanyNumber */
  AUCompanyNumber = "AUCompanyNumber",
  /** ATIdentityCard */
  ATIdentityCard = "ATIdentityCard",
  /** ATTaxIdentificationNumber */
  ATTaxIdentificationNumber = "ATTaxIdentificationNumber",
  /** ATValueAddedTaxNumber */
  ATValueAddedTaxNumber = "ATValueAddedTaxNumber",
  /** AzureDocumentDBAuthKey */
  AzureDocumentDBAuthKey = "AzureDocumentDBAuthKey",
  /** AzureIaasDatabaseConnectionAndSQLString */
  AzureIaasDatabaseConnectionAndSQLString = "AzureIAASDatabaseConnectionAndSQLString",
  /** AzureIoTConnectionString */
  AzureIoTConnectionString = "AzureIoTConnectionString",
  /** AzurePublishSettingPassword */
  AzurePublishSettingPassword = "AzurePublishSettingPassword",
  /** AzureRedisCacheString */
  AzureRedisCacheString = "AzureRedisCacheString",
  /** AzureSAS */
  AzureSAS = "AzureSAS",
  /** AzureServiceBusString */
  AzureServiceBusString = "AzureServiceBusString",
  /** AzureStorageAccountKey */
  AzureStorageAccountKey = "AzureStorageAccountKey",
  /** AzureStorageAccountGeneric */
  AzureStorageAccountGeneric = "AzureStorageAccountGeneric",
  /** BENationalNumber */
  BENationalNumber = "BENationalNumber",
  /** BENationalNumberV2 */
  BENationalNumberV2 = "BENationalNumberV2",
  /** BEValueAddedTaxNumber */
  BEValueAddedTaxNumber = "BEValueAddedTaxNumber",
  /** BrcpfNumber */
  BrcpfNumber = "BRCPFNumber",
  /** BRLegalEntityNumber */
  BRLegalEntityNumber = "BRLegalEntityNumber",
  /** BRNationalIdrg */
  BRNationalIdrg = "BRNationalIDRG",
  /** BGUniformCivilNumber */
  BGUniformCivilNumber = "BGUniformCivilNumber",
  /** CABankAccountNumber */
  CABankAccountNumber = "CABankAccountNumber",
  /** CADriversLicenseNumber */
  CADriversLicenseNumber = "CADriversLicenseNumber",
  /** CAHealthServiceNumber */
  CAHealthServiceNumber = "CAHealthServiceNumber",
  /** CAPassportNumber */
  CAPassportNumber = "CAPassportNumber",
  /** CAPersonalHealthIdentification */
  CAPersonalHealthIdentification = "CAPersonalHealthIdentification",
  /** CASocialInsuranceNumber */
  CASocialInsuranceNumber = "CASocialInsuranceNumber",
  /** CLIdentityCardNumber */
  CLIdentityCardNumber = "CLIdentityCardNumber",
  /** CNResidentIdentityCardNumber */
  CNResidentIdentityCardNumber = "CNResidentIdentityCardNumber",
  /** CreditCardNumber */
  CreditCardNumber = "CreditCardNumber",
  /** HRIdentityCardNumber */
  HRIdentityCardNumber = "HRIdentityCardNumber",
  /** HRNationalIDNumber */
  HRNationalIDNumber = "HRNationalIDNumber",
  /** HRPersonalIdentificationNumber */
  HRPersonalIdentificationNumber = "HRPersonalIdentificationNumber",
  /** HRPersonalIdentificationOIBNumberV2 */
  HRPersonalIdentificationOIBNumberV2 = "HRPersonalIdentificationOIBNumberV2",
  /** CYIdentityCard */
  CYIdentityCard = "CYIdentityCard",
  /** CYTaxIdentificationNumber */
  CYTaxIdentificationNumber = "CYTaxIdentificationNumber",
  /** CZPersonalIdentityNumber */
  CZPersonalIdentityNumber = "CZPersonalIdentityNumber",
  /** CZPersonalIdentityV2 */
  CZPersonalIdentityV2 = "CZPersonalIdentityV2",
  /** DKPersonalIdentificationNumber */
  DKPersonalIdentificationNumber = "DKPersonalIdentificationNumber",
  /** DKPersonalIdentificationV2 */
  DKPersonalIdentificationV2 = "DKPersonalIdentificationV2",
  /** DrugEnforcementAgencyNumber */
  DrugEnforcementAgencyNumber = "DrugEnforcementAgencyNumber",
  /** EEPersonalIdentificationCode */
  EEPersonalIdentificationCode = "EEPersonalIdentificationCode",
  /** EUDebitCardNumber */
  EUDebitCardNumber = "EUDebitCardNumber",
  /** EUDriversLicenseNumber */
  EUDriversLicenseNumber = "EUDriversLicenseNumber",
  /** EugpsCoordinates */
  EugpsCoordinates = "EUGPSCoordinates",
  /** EUNationalIdentificationNumber */
  EUNationalIdentificationNumber = "EUNationalIdentificationNumber",
  /** EUPassportNumber */
  EUPassportNumber = "EUPassportNumber",
  /** EUSocialSecurityNumber */
  EUSocialSecurityNumber = "EUSocialSecurityNumber",
  /** EUTaxIdentificationNumber */
  EUTaxIdentificationNumber = "EUTaxIdentificationNumber",
  /** FIEuropeanHealthNumber */
  FIEuropeanHealthNumber = "FIEuropeanHealthNumber",
  /** FINationalID */
  FINationalID = "FINationalID",
  /** FINationalIDV2 */
  FINationalIDV2 = "FINationalIDV2",
  /** FIPassportNumber */
  FIPassportNumber = "FIPassportNumber",
  /** FRDriversLicenseNumber */
  FRDriversLicenseNumber = "FRDriversLicenseNumber",
  /** FRHealthInsuranceNumber */
  FRHealthInsuranceNumber = "FRHealthInsuranceNumber",
  /** FRNationalID */
  FRNationalID = "FRNationalID",
  /** FRPassportNumber */
  FRPassportNumber = "FRPassportNumber",
  /** FRSocialSecurityNumber */
  FRSocialSecurityNumber = "FRSocialSecurityNumber",
  /** FRTaxIdentificationNumber */
  FRTaxIdentificationNumber = "FRTaxIdentificationNumber",
  /** FRValueAddedTaxNumber */
  FRValueAddedTaxNumber = "FRValueAddedTaxNumber",
  /** DEDriversLicenseNumber */
  DEDriversLicenseNumber = "DEDriversLicenseNumber",
  /** DEPassportNumber */
  DEPassportNumber = "DEPassportNumber",
  /** DEIdentityCardNumber */
  DEIdentityCardNumber = "DEIdentityCardNumber",
  /** DETaxIdentificationNumber */
  DETaxIdentificationNumber = "DETaxIdentificationNumber",
  /** DEValueAddedNumber */
  DEValueAddedNumber = "DEValueAddedNumber",
  /** GRNationalIDCard */
  GRNationalIDCard = "GRNationalIDCard",
  /** GRNationalIDV2 */
  GRNationalIDV2 = "GRNationalIDV2",
  /** GRTaxIdentificationNumber */
  GRTaxIdentificationNumber = "GRTaxIdentificationNumber",
  /** HKIdentityCardNumber */
  HKIdentityCardNumber = "HKIdentityCardNumber",
  /** HUValueAddedNumber */
  HUValueAddedNumber = "HUValueAddedNumber",
  /** HUPersonalIdentificationNumber */
  HUPersonalIdentificationNumber = "HUPersonalIdentificationNumber",
  /** HUTaxIdentificationNumber */
  HUTaxIdentificationNumber = "HUTaxIdentificationNumber",
  /** INPermanentAccount */
  INPermanentAccount = "INPermanentAccount",
  /** INUniqueIdentificationNumber */
  INUniqueIdentificationNumber = "INUniqueIdentificationNumber",
  /** IDIdentityCardNumber */
  IDIdentityCardNumber = "IDIdentityCardNumber",
  /** InternationalBankingAccountNumber */
  InternationalBankingAccountNumber = "InternationalBankingAccountNumber",
  /** IEPersonalPublicServiceNumber */
  IEPersonalPublicServiceNumber = "IEPersonalPublicServiceNumber",
  /** IEPersonalPublicServiceNumberV2 */
  IEPersonalPublicServiceNumberV2 = "IEPersonalPublicServiceNumberV2",
  /** ILBankAccountNumber */
  ILBankAccountNumber = "ILBankAccountNumber",
  /** ILNationalID */
  ILNationalID = "ILNationalID",
  /** ITDriversLicenseNumber */
  ITDriversLicenseNumber = "ITDriversLicenseNumber",
  /** ITFiscalCode */
  ITFiscalCode = "ITFiscalCode",
  /** ITValueAddedTaxNumber */
  ITValueAddedTaxNumber = "ITValueAddedTaxNumber",
  /** JPBankAccountNumber */
  JPBankAccountNumber = "JPBankAccountNumber",
  /** JPDriversLicenseNumber */
  JPDriversLicenseNumber = "JPDriversLicenseNumber",
  /** JPPassportNumber */
  JPPassportNumber = "JPPassportNumber",
  /** JPResidentRegistrationNumber */
  JPResidentRegistrationNumber = "JPResidentRegistrationNumber",
  /** JPSocialInsuranceNumber */
  JPSocialInsuranceNumber = "JPSocialInsuranceNumber",
  /** JPMyNumberCorporate */
  JPMyNumberCorporate = "JPMyNumberCorporate",
  /** JPMyNumberPersonal */
  JPMyNumberPersonal = "JPMyNumberPersonal",
  /** JPResidenceCardNumber */
  JPResidenceCardNumber = "JPResidenceCardNumber",
  /** LVPersonalCode */
  LVPersonalCode = "LVPersonalCode",
  /** LTPersonalCode */
  LTPersonalCode = "LTPersonalCode",
  /** LUNationalIdentificationNumberNatural */
  LUNationalIdentificationNumberNatural = "LUNationalIdentificationNumberNatural",
  /** LUNationalIdentificationNumberNonNatural */
  LUNationalIdentificationNumberNonNatural = "LUNationalIdentificationNumberNonNatural",
  /** MYIdentityCardNumber */
  MYIdentityCardNumber = "MYIdentityCardNumber",
  /** MTIdentityCardNumber */
  MTIdentityCardNumber = "MTIdentityCardNumber",
  /** MTTaxIDNumber */
  MTTaxIDNumber = "MTTaxIDNumber",
  /** NLCitizensServiceNumber */
  NLCitizensServiceNumber = "NLCitizensServiceNumber",
  /** NLCitizensServiceNumberV2 */
  NLCitizensServiceNumberV2 = "NLCitizensServiceNumberV2",
  /** NLTaxIdentificationNumber */
  NLTaxIdentificationNumber = "NLTaxIdentificationNumber",
  /** NLValueAddedTaxNumber */
  NLValueAddedTaxNumber = "NLValueAddedTaxNumber",
  /** NZBankAccountNumber */
  NZBankAccountNumber = "NZBankAccountNumber",
  /** NZDriversLicenseNumber */
  NZDriversLicenseNumber = "NZDriversLicenseNumber",
  /** NZInlandRevenueNumber */
  NZInlandRevenueNumber = "NZInlandRevenueNumber",
  /** NZMinistryOfHealthNumber */
  NZMinistryOfHealthNumber = "NZMinistryOfHealthNumber",
  /** NZSocialWelfareNumber */
  NZSocialWelfareNumber = "NZSocialWelfareNumber",
  /** NOIdentityNumber */
  NOIdentityNumber = "NOIdentityNumber",
  /** PHUnifiedMultiPurposeIDNumber */
  PHUnifiedMultiPurposeIDNumber = "PHUnifiedMultiPurposeIDNumber",
  /** PLIdentityCard */
  PLIdentityCard = "PLIdentityCard",
  /** PLNationalID */
  PLNationalID = "PLNationalID",
  /** PLNationalIDV2 */
  PLNationalIDV2 = "PLNationalIDV2",
  /** PLPassportNumber */
  PLPassportNumber = "PLPassportNumber",
  /** PLTaxIdentificationNumber */
  PLTaxIdentificationNumber = "PLTaxIdentificationNumber",
  /** PlregonNumber */
  PlregonNumber = "PLREGONNumber",
  /** PTCitizenCardNumber */
  PTCitizenCardNumber = "PTCitizenCardNumber",
  /** PTCitizenCardNumberV2 */
  PTCitizenCardNumberV2 = "PTCitizenCardNumberV2",
  /** PTTaxIdentificationNumber */
  PTTaxIdentificationNumber = "PTTaxIdentificationNumber",
  /** ROPersonalNumericalCode */
  ROPersonalNumericalCode = "ROPersonalNumericalCode",
  /** RUPassportNumberDomestic */
  RUPassportNumberDomestic = "RUPassportNumberDomestic",
  /** RUPassportNumberInternational */
  RUPassportNumberInternational = "RUPassportNumberInternational",
  /** SANationalID */
  SANationalID = "SANationalID",
  /** SGNationalRegistrationIdentityCardNumber */
  SGNationalRegistrationIdentityCardNumber = "SGNationalRegistrationIdentityCardNumber",
  /** SKPersonalNumber */
  SKPersonalNumber = "SKPersonalNumber",
  /** SITaxIdentificationNumber */
  SITaxIdentificationNumber = "SITaxIdentificationNumber",
  /** SIUniqueMasterCitizenNumber */
  SIUniqueMasterCitizenNumber = "SIUniqueMasterCitizenNumber",
  /** ZAIdentificationNumber */
  ZAIdentificationNumber = "ZAIdentificationNumber",
  /** KRResidentRegistrationNumber */
  KRResidentRegistrationNumber = "KRResidentRegistrationNumber",
  /** Esdni */
  Esdni = "ESDNI",
  /** ESSocialSecurityNumber */
  ESSocialSecurityNumber = "ESSocialSecurityNumber",
  /** ESTaxIdentificationNumber */
  ESTaxIdentificationNumber = "ESTaxIdentificationNumber",
  /** SQLServerConnectionString */
  SQLServerConnectionString = "SQLServerConnectionString",
  /** SENationalID */
  SENationalID = "SENationalID",
  /** SENationalIDV2 */
  SENationalIDV2 = "SENationalIDV2",
  /** SEPassportNumber */
  SEPassportNumber = "SEPassportNumber",
  /** SETaxIdentificationNumber */
  SETaxIdentificationNumber = "SETaxIdentificationNumber",
  /** SwiftCode */
  SwiftCode = "SWIFTCode",
  /** CHSocialSecurityNumber */
  CHSocialSecurityNumber = "CHSocialSecurityNumber",
  /** TWNationalID */
  TWNationalID = "TWNationalID",
  /** TWPassportNumber */
  TWPassportNumber = "TWPassportNumber",
  /** TWResidentCertificate */
  TWResidentCertificate = "TWResidentCertificate",
  /** THPopulationIdentificationCode */
  THPopulationIdentificationCode = "THPopulationIdentificationCode",
  /** TRNationalIdentificationNumber */
  TRNationalIdentificationNumber = "TRNationalIdentificationNumber",
  /** UKDriversLicenseNumber */
  UKDriversLicenseNumber = "UKDriversLicenseNumber",
  /** UKElectoralRollNumber */
  UKElectoralRollNumber = "UKElectoralRollNumber",
  /** UKNationalHealthNumber */
  UKNationalHealthNumber = "UKNationalHealthNumber",
  /** UKNationalInsuranceNumber */
  UKNationalInsuranceNumber = "UKNationalInsuranceNumber",
  /** UKUniqueTaxpayerNumber */
  UKUniqueTaxpayerNumber = "UKUniqueTaxpayerNumber",
  /** UsukPassportNumber */
  UsukPassportNumber = "USUKPassportNumber",
  /** USBankAccountNumber */
  USBankAccountNumber = "USBankAccountNumber",
  /** USDriversLicenseNumber */
  USDriversLicenseNumber = "USDriversLicenseNumber",
  /** USIndividualTaxpayerIdentification */
  USIndividualTaxpayerIdentification = "USIndividualTaxpayerIdentification",
  /** USSocialSecurityNumber */
  USSocialSecurityNumber = "USSocialSecurityNumber",
  /** UAPassportNumberDomestic */
  UAPassportNumberDomestic = "UAPassportNumberDomestic",
  /** UAPassportNumberInternational */
  UAPassportNumberInternational = "UAPassportNumberInternational",
  /** Organization */
  Organization = "Organization",
  /** Email */
  Email = "Email",
  /** URL */
  URL = "URL",
  /** Age */
  Age = "Age",
  /** PhoneNumber */
  PhoneNumber = "PhoneNumber",
  /** IPAddress */
  IPAddress = "IPAddress",
  /** Date */
  Date = "Date",
  /** Person */
  Person = "Person",
  /** Address */
  Address = "Address",
  /** All */
  All = "All",
  /** Default */
  Default = "Default"
}

/**
 * Defines values for PiiEntityCategory. \
 * {@link KnownPiiEntityCategory} can be used interchangeably with PiiEntityCategory,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **ABARoutingNumber** \
 * **ARNationalIdentityNumber** \
 * **AUBankAccountNumber** \
 * **AUDriversLicenseNumber** \
 * **AUMedicalAccountNumber** \
 * **AUPassportNumber** \
 * **AUTaxFileNumber** \
 * **AUBusinessNumber** \
 * **AUCompanyNumber** \
 * **ATIdentityCard** \
 * **ATTaxIdentificationNumber** \
 * **ATValueAddedTaxNumber** \
 * **AzureDocumentDBAuthKey** \
 * **AzureIAASDatabaseConnectionAndSQLString** \
 * **AzureIoTConnectionString** \
 * **AzurePublishSettingPassword** \
 * **AzureRedisCacheString** \
 * **AzureSAS** \
 * **AzureServiceBusString** \
 * **AzureStorageAccountKey** \
 * **AzureStorageAccountGeneric** \
 * **BENationalNumber** \
 * **BENationalNumberV2** \
 * **BEValueAddedTaxNumber** \
 * **BRCPFNumber** \
 * **BRLegalEntityNumber** \
 * **BRNationalIDRG** \
 * **BGUniformCivilNumber** \
 * **CABankAccountNumber** \
 * **CADriversLicenseNumber** \
 * **CAHealthServiceNumber** \
 * **CAPassportNumber** \
 * **CAPersonalHealthIdentification** \
 * **CASocialInsuranceNumber** \
 * **CLIdentityCardNumber** \
 * **CNResidentIdentityCardNumber** \
 * **CreditCardNumber** \
 * **HRIdentityCardNumber** \
 * **HRNationalIDNumber** \
 * **HRPersonalIdentificationNumber** \
 * **HRPersonalIdentificationOIBNumberV2** \
 * **CYIdentityCard** \
 * **CYTaxIdentificationNumber** \
 * **CZPersonalIdentityNumber** \
 * **CZPersonalIdentityV2** \
 * **DKPersonalIdentificationNumber** \
 * **DKPersonalIdentificationV2** \
 * **DrugEnforcementAgencyNumber** \
 * **EEPersonalIdentificationCode** \
 * **EUDebitCardNumber** \
 * **EUDriversLicenseNumber** \
 * **EUGPSCoordinates** \
 * **EUNationalIdentificationNumber** \
 * **EUPassportNumber** \
 * **EUSocialSecurityNumber** \
 * **EUTaxIdentificationNumber** \
 * **FIEuropeanHealthNumber** \
 * **FINationalID** \
 * **FINationalIDV2** \
 * **FIPassportNumber** \
 * **FRDriversLicenseNumber** \
 * **FRHealthInsuranceNumber** \
 * **FRNationalID** \
 * **FRPassportNumber** \
 * **FRSocialSecurityNumber** \
 * **FRTaxIdentificationNumber** \
 * **FRValueAddedTaxNumber** \
 * **DEDriversLicenseNumber** \
 * **DEPassportNumber** \
 * **DEIdentityCardNumber** \
 * **DETaxIdentificationNumber** \
 * **DEValueAddedNumber** \
 * **GRNationalIDCard** \
 * **GRNationalIDV2** \
 * **GRTaxIdentificationNumber** \
 * **HKIdentityCardNumber** \
 * **HUValueAddedNumber** \
 * **HUPersonalIdentificationNumber** \
 * **HUTaxIdentificationNumber** \
 * **INPermanentAccount** \
 * **INUniqueIdentificationNumber** \
 * **IDIdentityCardNumber** \
 * **InternationalBankingAccountNumber** \
 * **IEPersonalPublicServiceNumber** \
 * **IEPersonalPublicServiceNumberV2** \
 * **ILBankAccountNumber** \
 * **ILNationalID** \
 * **ITDriversLicenseNumber** \
 * **ITFiscalCode** \
 * **ITValueAddedTaxNumber** \
 * **JPBankAccountNumber** \
 * **JPDriversLicenseNumber** \
 * **JPPassportNumber** \
 * **JPResidentRegistrationNumber** \
 * **JPSocialInsuranceNumber** \
 * **JPMyNumberCorporate** \
 * **JPMyNumberPersonal** \
 * **JPResidenceCardNumber** \
 * **LVPersonalCode** \
 * **LTPersonalCode** \
 * **LUNationalIdentificationNumberNatural** \
 * **LUNationalIdentificationNumberNonNatural** \
 * **MYIdentityCardNumber** \
 * **MTIdentityCardNumber** \
 * **MTTaxIDNumber** \
 * **NLCitizensServiceNumber** \
 * **NLCitizensServiceNumberV2** \
 * **NLTaxIdentificationNumber** \
 * **NLValueAddedTaxNumber** \
 * **NZBankAccountNumber** \
 * **NZDriversLicenseNumber** \
 * **NZInlandRevenueNumber** \
 * **NZMinistryOfHealthNumber** \
 * **NZSocialWelfareNumber** \
 * **NOIdentityNumber** \
 * **PHUnifiedMultiPurposeIDNumber** \
 * **PLIdentityCard** \
 * **PLNationalID** \
 * **PLNationalIDV2** \
 * **PLPassportNumber** \
 * **PLTaxIdentificationNumber** \
 * **PLREGONNumber** \
 * **PTCitizenCardNumber** \
 * **PTCitizenCardNumberV2** \
 * **PTTaxIdentificationNumber** \
 * **ROPersonalNumericalCode** \
 * **RUPassportNumberDomestic** \
 * **RUPassportNumberInternational** \
 * **SANationalID** \
 * **SGNationalRegistrationIdentityCardNumber** \
 * **SKPersonalNumber** \
 * **SITaxIdentificationNumber** \
 * **SIUniqueMasterCitizenNumber** \
 * **ZAIdentificationNumber** \
 * **KRResidentRegistrationNumber** \
 * **ESDNI** \
 * **ESSocialSecurityNumber** \
 * **ESTaxIdentificationNumber** \
 * **SQLServerConnectionString** \
 * **SENationalID** \
 * **SENationalIDV2** \
 * **SEPassportNumber** \
 * **SETaxIdentificationNumber** \
 * **SWIFTCode** \
 * **CHSocialSecurityNumber** \
 * **TWNationalID** \
 * **TWPassportNumber** \
 * **TWResidentCertificate** \
 * **THPopulationIdentificationCode** \
 * **TRNationalIdentificationNumber** \
 * **UKDriversLicenseNumber** \
 * **UKElectoralRollNumber** \
 * **UKNationalHealthNumber** \
 * **UKNationalInsuranceNumber** \
 * **UKUniqueTaxpayerNumber** \
 * **USUKPassportNumber** \
 * **USBankAccountNumber** \
 * **USDriversLicenseNumber** \
 * **USIndividualTaxpayerIdentification** \
 * **USSocialSecurityNumber** \
 * **UAPassportNumberDomestic** \
 * **UAPassportNumberInternational** \
 * **Organization** \
 * **Email** \
 * **URL** \
 * **Age** \
 * **PhoneNumber** \
 * **IPAddress** \
 * **Date** \
 * **Person** \
 * **Address** \
 * **All** \
 * **Default**
 */
export type PiiEntityCategory = string;

/** Known values of {@link ClassificationType} that the service accepts. */
export enum KnownClassificationType {
  /** Single */
  Single = "Single",
  /** Multi */
  Multi = "Multi"
}

/**
 * Defines values for ClassificationType. \
 * {@link KnownClassificationType} can be used interchangeably with ClassificationType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Single** \
 * **Multi**
 */
export type ClassificationType = string;

/** Known values of {@link ResolutionKind} that the service accepts. */
export enum KnownResolutionKind {
  /** BooleanResolution */
  BooleanResolution = "BooleanResolution",
  /** DateTimeResolution */
  DateTimeResolution = "DateTimeResolution",
  /** NumberResolution */
  NumberResolution = "NumberResolution",
  /** OrdinalResolution */
  OrdinalResolution = "OrdinalResolution",
  /** SpeedResolution */
  SpeedResolution = "SpeedResolution",
  /** WeightResolution */
  WeightResolution = "WeightResolution",
  /** LengthResolution */
  LengthResolution = "LengthResolution",
  /** VolumeResolution */
  VolumeResolution = "VolumeResolution",
  /** AreaResolution */
  AreaResolution = "AreaResolution",
  /** AgeResolution */
  AgeResolution = "AgeResolution",
  /** InformationResolution */
  InformationResolution = "InformationResolution",
  /** TemperatureResolution */
  TemperatureResolution = "TemperatureResolution",
  /** CurrencyResolution */
  CurrencyResolution = "CurrencyResolution",
  /** NumericRangeResolution */
  NumericRangeResolution = "NumericRangeResolution",
  /** TemporalSpanResolution */
  TemporalSpanResolution = "TemporalSpanResolution"
}

/**
 * Defines values for ResolutionKind. \
 * {@link KnownResolutionKind} can be used interchangeably with ResolutionKind,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **BooleanResolution** \
 * **DateTimeResolution** \
 * **NumberResolution** \
 * **OrdinalResolution** \
 * **SpeedResolution** \
 * **WeightResolution** \
 * **LengthResolution** \
 * **VolumeResolution** \
 * **AreaResolution** \
 * **AgeResolution** \
 * **InformationResolution** \
 * **TemperatureResolution** \
 * **CurrencyResolution** \
 * **NumericRangeResolution** \
 * **TemporalSpanResolution**
 */
export type ResolutionKind = string;

/** Known values of {@link WarningCode} that the service accepts. */
export enum KnownWarningCode {
  /** LongWordsInDocument */
  LongWordsInDocument = "LongWordsInDocument",
  /** DocumentTruncated */
  DocumentTruncated = "DocumentTruncated"
}

/**
 * Defines values for WarningCode. \
 * {@link KnownWarningCode} can be used interchangeably with WarningCode,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **LongWordsInDocument** \
 * **DocumentTruncated**
 */
export type WarningCode = string;

/** Known values of {@link ScriptKind} that the service accepts. */
export enum KnownScriptKind {
  /** Latin */
  Latin = "Latin"
}

/**
 * Defines values for ScriptKind. \
 * {@link KnownScriptKind} can be used interchangeably with ScriptKind,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Latin**
 */
export type ScriptKind = string;

/** Known values of {@link FhirVersion} that the service accepts. */
export enum KnownFhirVersion {
  /** Four01 */
  Four01 = "4.0.1"
}

/**
 * Defines values for FhirVersion. \
 * {@link KnownFhirVersion} can be used interchangeably with FhirVersion,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **4.0.1**
 */
export type FhirVersion = string;

/** Known values of {@link DocumentType} that the service accepts. */
export enum KnownDocumentType {
  /** None */
  None = "None",
  /** ClinicalTrial */
  ClinicalTrial = "ClinicalTrial",
  /** DischargeSummary */
  DischargeSummary = "DischargeSummary",
  /** ProgressNote */
  ProgressNote = "ProgressNote",
  /** HistoryAndPhysical */
  HistoryAndPhysical = "HistoryAndPhysical",
  /** Consult */
  Consult = "Consult",
  /** Imaging */
  Imaging = "Imaging",
  /** Pathology */
  Pathology = "Pathology",
  /** ProcedureNote */
  ProcedureNote = "ProcedureNote"
}

/**
 * Defines values for DocumentType. \
 * {@link KnownDocumentType} can be used interchangeably with DocumentType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **None** \
 * **ClinicalTrial** \
 * **DischargeSummary** \
 * **ProgressNote** \
 * **HistoryAndPhysical** \
 * **Consult** \
 * **Imaging** \
 * **Pathology** \
 * **ProcedureNote**
 */
export type DocumentType = string;

/** Known values of {@link HealthcareEntityCategory} that the service accepts. */
export enum KnownHealthcareEntityCategory {
  /** BodyStructure */
  BodyStructure = "BodyStructure",
  /** Age */
  Age = "Age",
  /** Gender */
  Gender = "Gender",
  /** ExaminationName */
  ExaminationName = "ExaminationName",
  /** Date */
  Date = "Date",
  /** Direction */
  Direction = "Direction",
  /** Frequency */
  Frequency = "Frequency",
  /** MeasurementValue */
  MeasurementValue = "MeasurementValue",
  /** MeasurementUnit */
  MeasurementUnit = "MeasurementUnit",
  /** RelationalOperator */
  RelationalOperator = "RelationalOperator",
  /** Time */
  Time = "Time",
  /** GeneOrProtein */
  GeneOrProtein = "GeneOrProtein",
  /** Variant */
  Variant = "Variant",
  /** AdministrativeEvent */
  AdministrativeEvent = "AdministrativeEvent",
  /** CareEnvironment */
  CareEnvironment = "CareEnvironment",
  /** HealthcareProfession */
  HealthcareProfession = "HealthcareProfession",
  /** Diagnosis */
  Diagnosis = "Diagnosis",
  /** SymptomOrSign */
  SymptomOrSign = "SymptomOrSign",
  /** ConditionQualifier */
  ConditionQualifier = "ConditionQualifier",
  /** MedicationClass */
  MedicationClass = "MedicationClass",
  /** MedicationName */
  MedicationName = "MedicationName",
  /** Dosage */
  Dosage = "Dosage",
  /** MedicationForm */
  MedicationForm = "MedicationForm",
  /** MedicationRoute */
  MedicationRoute = "MedicationRoute",
  /** FamilyRelation */
  FamilyRelation = "FamilyRelation",
  /** TreatmentName */
  TreatmentName = "TreatmentName"
}

/**
 * Defines values for HealthcareEntityCategory. \
 * {@link KnownHealthcareEntityCategory} can be used interchangeably with HealthcareEntityCategory,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **BodyStructure** \
 * **Age** \
 * **Gender** \
 * **ExaminationName** \
 * **Date** \
 * **Direction** \
 * **Frequency** \
 * **MeasurementValue** \
 * **MeasurementUnit** \
 * **RelationalOperator** \
 * **Time** \
 * **GeneOrProtein** \
 * **Variant** \
 * **AdministrativeEvent** \
 * **CareEnvironment** \
 * **HealthcareProfession** \
 * **Diagnosis** \
 * **SymptomOrSign** \
 * **ConditionQualifier** \
 * **MedicationClass** \
 * **MedicationName** \
 * **Dosage** \
 * **MedicationForm** \
 * **MedicationRoute** \
 * **FamilyRelation** \
 * **TreatmentName**
 */
export type HealthcareEntityCategory = string;

/** Known values of {@link RelationType} that the service accepts. */
export enum KnownRelationType {
  /** Abbreviation */
  Abbreviation = "Abbreviation",
  /** DirectionOfBodyStructure */
  DirectionOfBodyStructure = "DirectionOfBodyStructure",
  /** DirectionOfCondition */
  DirectionOfCondition = "DirectionOfCondition",
  /** DirectionOfExamination */
  DirectionOfExamination = "DirectionOfExamination",
  /** DirectionOfTreatment */
  DirectionOfTreatment = "DirectionOfTreatment",
  /** DosageOfMedication */
  DosageOfMedication = "DosageOfMedication",
  /** FormOfMedication */
  FormOfMedication = "FormOfMedication",
  /** FrequencyOfMedication */
  FrequencyOfMedication = "FrequencyOfMedication",
  /** FrequencyOfTreatment */
  FrequencyOfTreatment = "FrequencyOfTreatment",
  /** QualifierOfCondition */
  QualifierOfCondition = "QualifierOfCondition",
  /** RelationOfExamination */
  RelationOfExamination = "RelationOfExamination",
  /** RouteOfMedication */
  RouteOfMedication = "RouteOfMedication",
  /** TimeOfCondition */
  TimeOfCondition = "TimeOfCondition",
  /** TimeOfEvent */
  TimeOfEvent = "TimeOfEvent",
  /** TimeOfExamination */
  TimeOfExamination = "TimeOfExamination",
  /** TimeOfMedication */
  TimeOfMedication = "TimeOfMedication",
  /** TimeOfTreatment */
  TimeOfTreatment = "TimeOfTreatment",
  /** UnitOfCondition */
  UnitOfCondition = "UnitOfCondition",
  /** UnitOfExamination */
  UnitOfExamination = "UnitOfExamination",
  /** ValueOfCondition */
  ValueOfCondition = "ValueOfCondition",
  /** ValueOfExamination */
  ValueOfExamination = "ValueOfExamination"
}

/**
 * Defines values for RelationType. \
 * {@link KnownRelationType} can be used interchangeably with RelationType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Abbreviation** \
 * **DirectionOfBodyStructure** \
 * **DirectionOfCondition** \
 * **DirectionOfExamination** \
 * **DirectionOfTreatment** \
 * **DosageOfMedication** \
 * **FormOfMedication** \
 * **FrequencyOfMedication** \
 * **FrequencyOfTreatment** \
 * **QualifierOfCondition** \
 * **RelationOfExamination** \
 * **RouteOfMedication** \
 * **TimeOfCondition** \
 * **TimeOfEvent** \
 * **TimeOfExamination** \
 * **TimeOfMedication** \
 * **TimeOfTreatment** \
 * **UnitOfCondition** \
 * **UnitOfExamination** \
 * **ValueOfCondition** \
 * **ValueOfExamination**
 */
export type RelationType = string;

/** Known values of {@link ExtractiveSummarizationOrderingCriteria} that the service accepts. */
export enum KnownExtractiveSummarizationOrderingCriteria {
  /** Indicates that results should be sorted in order of appearance in the text. */
  Offset = "Offset",
  /** Indicates that results should be sorted in order of importance (i.e. rank score) according to the model. */
  Rank = "Rank"
}

/**
 * Defines values for ExtractiveSummarizationOrderingCriteria. \
 * {@link KnownExtractiveSummarizationOrderingCriteria} can be used interchangeably with ExtractiveSummarizationOrderingCriteria,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Offset**: Indicates that results should be sorted in order of appearance in the text. \
 * **Rank**: Indicates that results should be sorted in order of importance (i.e. rank score) according to the model.
 */
export type ExtractiveSummarizationOrderingCriteria = string;

/** Known values of {@link AgeUnit} that the service accepts. */
export enum KnownAgeUnit {
  /** Unspecified */
  Unspecified = "Unspecified",
  /** Year */
  Year = "Year",
  /** Month */
  Month = "Month",
  /** Week */
  Week = "Week",
  /** Day */
  Day = "Day"
}

/**
 * Defines values for AgeUnit. \
 * {@link KnownAgeUnit} can be used interchangeably with AgeUnit,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Unspecified** \
 * **Year** \
 * **Month** \
 * **Week** \
 * **Day**
 */
export type AgeUnit = string;

/** Known values of {@link VolumeUnit} that the service accepts. */
export enum KnownVolumeUnit {
  /** Unspecified */
  Unspecified = "Unspecified",
  /** CubicMeter */
  CubicMeter = "CubicMeter",
  /** CubicCentimeter */
  CubicCentimeter = "CubicCentimeter",
  /** CubicMillimeter */
  CubicMillimeter = "CubicMillimeter",
  /** Hectoliter */
  Hectoliter = "Hectoliter",
  /** Decaliter */
  Decaliter = "Decaliter",
  /** Liter */
  Liter = "Liter",
  /** Centiliter */
  Centiliter = "Centiliter",
  /** Milliliter */
  Milliliter = "Milliliter",
  /** CubicYard */
  CubicYard = "CubicYard",
  /** CubicInch */
  CubicInch = "CubicInch",
  /** CubicFoot */
  CubicFoot = "CubicFoot",
  /** CubicMile */
  CubicMile = "CubicMile",
  /** FluidOunce */
  FluidOunce = "FluidOunce",
  /** Teaspoon */
  Teaspoon = "Teaspoon",
  /** Tablespoon */
  Tablespoon = "Tablespoon",
  /** Pint */
  Pint = "Pint",
  /** Quart */
  Quart = "Quart",
  /** Cup */
  Cup = "Cup",
  /** Gill */
  Gill = "Gill",
  /** Pinch */
  Pinch = "Pinch",
  /** FluidDram */
  FluidDram = "FluidDram",
  /** Barrel */
  Barrel = "Barrel",
  /** Minim */
  Minim = "Minim",
  /** Cord */
  Cord = "Cord",
  /** Peck */
  Peck = "Peck",
  /** Bushel */
  Bushel = "Bushel",
  /** Hogshead */
  Hogshead = "Hogshead"
}

/**
 * Defines values for VolumeUnit. \
 * {@link KnownVolumeUnit} can be used interchangeably with VolumeUnit,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Unspecified** \
 * **CubicMeter** \
 * **CubicCentimeter** \
 * **CubicMillimeter** \
 * **Hectoliter** \
 * **Decaliter** \
 * **Liter** \
 * **Centiliter** \
 * **Milliliter** \
 * **CubicYard** \
 * **CubicInch** \
 * **CubicFoot** \
 * **CubicMile** \
 * **FluidOunce** \
 * **Teaspoon** \
 * **Tablespoon** \
 * **Pint** \
 * **Quart** \
 * **Cup** \
 * **Gill** \
 * **Pinch** \
 * **FluidDram** \
 * **Barrel** \
 * **Minim** \
 * **Cord** \
 * **Peck** \
 * **Bushel** \
 * **Hogshead**
 */
export type VolumeUnit = string;

/** Known values of {@link SpeedUnit} that the service accepts. */
export enum KnownSpeedUnit {
  /** Unspecified */
  Unspecified = "Unspecified",
  /** MetersPerSecond */
  MetersPerSecond = "MetersPerSecond",
  /** KilometersPerHour */
  KilometersPerHour = "KilometersPerHour",
  /** KilometersPerMinute */
  KilometersPerMinute = "KilometersPerMinute",
  /** KilometersPerSecond */
  KilometersPerSecond = "KilometersPerSecond",
  /** MilesPerHour */
  MilesPerHour = "MilesPerHour",
  /** Knot */
  Knot = "Knot",
  /** FootPerSecond */
  FootPerSecond = "FootPerSecond",
  /** FootPerMinute */
  FootPerMinute = "FootPerMinute",
  /** YardsPerMinute */
  YardsPerMinute = "YardsPerMinute",
  /** YardsPerSecond */
  YardsPerSecond = "YardsPerSecond",
  /** MetersPerMillisecond */
  MetersPerMillisecond = "MetersPerMillisecond",
  /** CentimetersPerMillisecond */
  CentimetersPerMillisecond = "CentimetersPerMillisecond",
  /** KilometersPerMillisecond */
  KilometersPerMillisecond = "KilometersPerMillisecond"
}

/**
 * Defines values for SpeedUnit. \
 * {@link KnownSpeedUnit} can be used interchangeably with SpeedUnit,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Unspecified** \
 * **MetersPerSecond** \
 * **KilometersPerHour** \
 * **KilometersPerMinute** \
 * **KilometersPerSecond** \
 * **MilesPerHour** \
 * **Knot** \
 * **FootPerSecond** \
 * **FootPerMinute** \
 * **YardsPerMinute** \
 * **YardsPerSecond** \
 * **MetersPerMillisecond** \
 * **CentimetersPerMillisecond** \
 * **KilometersPerMillisecond**
 */
export type SpeedUnit = string;

/** Known values of {@link AreaUnit} that the service accepts. */
export enum KnownAreaUnit {
  /** Unspecified */
  Unspecified = "Unspecified",
  /** SquareKilometer */
  SquareKilometer = "SquareKilometer",
  /** SquareHectometer */
  SquareHectometer = "SquareHectometer",
  /** SquareDecameter */
  SquareDecameter = "SquareDecameter",
  /** SquareDecimeter */
  SquareDecimeter = "SquareDecimeter",
  /** SquareMeter */
  SquareMeter = "SquareMeter",
  /** SquareCentimeter */
  SquareCentimeter = "SquareCentimeter",
  /** SquareMillimeter */
  SquareMillimeter = "SquareMillimeter",
  /** SquareInch */
  SquareInch = "SquareInch",
  /** SquareFoot */
  SquareFoot = "SquareFoot",
  /** SquareMile */
  SquareMile = "SquareMile",
  /** SquareYard */
  SquareYard = "SquareYard",
  /** Acre */
  Acre = "Acre"
}

/**
 * Defines values for AreaUnit. \
 * {@link KnownAreaUnit} can be used interchangeably with AreaUnit,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Unspecified** \
 * **SquareKilometer** \
 * **SquareHectometer** \
 * **SquareDecameter** \
 * **SquareDecimeter** \
 * **SquareMeter** \
 * **SquareCentimeter** \
 * **SquareMillimeter** \
 * **SquareInch** \
 * **SquareFoot** \
 * **SquareMile** \
 * **SquareYard** \
 * **Acre**
 */
export type AreaUnit = string;

/** Known values of {@link LengthUnit} that the service accepts. */
export enum KnownLengthUnit {
  /** Unspecified */
  Unspecified = "Unspecified",
  /** Kilometer */
  Kilometer = "Kilometer",
  /** Hectometer */
  Hectometer = "Hectometer",
  /** Decameter */
  Decameter = "Decameter",
  /** Meter */
  Meter = "Meter",
  /** Decimeter */
  Decimeter = "Decimeter",
  /** Centimeter */
  Centimeter = "Centimeter",
  /** Millimeter */
  Millimeter = "Millimeter",
  /** Micrometer */
  Micrometer = "Micrometer",
  /** Nanometer */
  Nanometer = "Nanometer",
  /** Picometer */
  Picometer = "Picometer",
  /** Mile */
  Mile = "Mile",
  /** Yard */
  Yard = "Yard",
  /** Inch */
  Inch = "Inch",
  /** Foot */
  Foot = "Foot",
  /** LightYear */
  LightYear = "LightYear",
  /** Pt */
  Pt = "Pt"
}

/**
 * Defines values for LengthUnit. \
 * {@link KnownLengthUnit} can be used interchangeably with LengthUnit,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Unspecified** \
 * **Kilometer** \
 * **Hectometer** \
 * **Decameter** \
 * **Meter** \
 * **Decimeter** \
 * **Centimeter** \
 * **Millimeter** \
 * **Micrometer** \
 * **Nanometer** \
 * **Picometer** \
 * **Mile** \
 * **Yard** \
 * **Inch** \
 * **Foot** \
 * **LightYear** \
 * **Pt**
 */
export type LengthUnit = string;

/** Known values of {@link InformationUnit} that the service accepts. */
export enum KnownInformationUnit {
  /** Unspecified */
  Unspecified = "Unspecified",
  /** Bit */
  Bit = "Bit",
  /** Kilobit */
  Kilobit = "Kilobit",
  /** Megabit */
  Megabit = "Megabit",
  /** Gigabit */
  Gigabit = "Gigabit",
  /** Terabit */
  Terabit = "Terabit",
  /** Petabit */
  Petabit = "Petabit",
  /** Byte */
  Byte = "Byte",
  /** Kilobyte */
  Kilobyte = "Kilobyte",
  /** Megabyte */
  Megabyte = "Megabyte",
  /** Gigabyte */
  Gigabyte = "Gigabyte",
  /** Terabyte */
  Terabyte = "Terabyte",
  /** Petabyte */
  Petabyte = "Petabyte"
}

/**
 * Defines values for InformationUnit. \
 * {@link KnownInformationUnit} can be used interchangeably with InformationUnit,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Unspecified** \
 * **Bit** \
 * **Kilobit** \
 * **Megabit** \
 * **Gigabit** \
 * **Terabit** \
 * **Petabit** \
 * **Byte** \
 * **Kilobyte** \
 * **Megabyte** \
 * **Gigabyte** \
 * **Terabyte** \
 * **Petabyte**
 */
export type InformationUnit = string;

/** Known values of {@link TemperatureUnit} that the service accepts. */
export enum KnownTemperatureUnit {
  /** Unspecified */
  Unspecified = "Unspecified",
  /** Fahrenheit */
  Fahrenheit = "Fahrenheit",
  /** Kelvin */
  Kelvin = "Kelvin",
  /** Rankine */
  Rankine = "Rankine",
  /** Celsius */
  Celsius = "Celsius"
}

/**
 * Defines values for TemperatureUnit. \
 * {@link KnownTemperatureUnit} can be used interchangeably with TemperatureUnit,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Unspecified** \
 * **Fahrenheit** \
 * **Kelvin** \
 * **Rankine** \
 * **Celsius**
 */
export type TemperatureUnit = string;

/** Known values of {@link WeightUnit} that the service accepts. */
export enum KnownWeightUnit {
  /** Unspecified */
  Unspecified = "Unspecified",
  /** Kilogram */
  Kilogram = "Kilogram",
  /** Gram */
  Gram = "Gram",
  /** Milligram */
  Milligram = "Milligram",
  /** Gallon */
  Gallon = "Gallon",
  /** MetricTon */
  MetricTon = "MetricTon",
  /** Ton */
  Ton = "Ton",
  /** Pound */
  Pound = "Pound",
  /** Ounce */
  Ounce = "Ounce",
  /** Grain */
  Grain = "Grain",
  /** PennyWeight */
  PennyWeight = "PennyWeight",
  /** LongTonBritish */
  LongTonBritish = "LongTonBritish",
  /** ShortTonUS */
  ShortTonUS = "ShortTonUS",
  /** ShortHundredWeightUS */
  ShortHundredWeightUS = "ShortHundredWeightUS",
  /** Stone */
  Stone = "Stone",
  /** Dram */
  Dram = "Dram"
}

/**
 * Defines values for WeightUnit. \
 * {@link KnownWeightUnit} can be used interchangeably with WeightUnit,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Unspecified** \
 * **Kilogram** \
 * **Gram** \
 * **Milligram** \
 * **Gallon** \
 * **MetricTon** \
 * **Ton** \
 * **Pound** \
 * **Ounce** \
 * **Grain** \
 * **PennyWeight** \
 * **LongTonBritish** \
 * **ShortTonUS** \
 * **ShortHundredWeightUS** \
 * **Stone** \
 * **Dram**
 */
export type WeightUnit = string;

/** Known values of {@link DateTimeSubKind} that the service accepts. */
export enum KnownDateTimeSubKind {
  /** Time */
  Time = "Time",
  /** Date */
  Date = "Date",
  /** DateTime */
  DateTime = "DateTime",
  /** Duration */
  Duration = "Duration",
  /** Set */
  Set = "Set"
}

/**
 * Defines values for DateTimeSubKind. \
 * {@link KnownDateTimeSubKind} can be used interchangeably with DateTimeSubKind,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Time** \
 * **Date** \
 * **DateTime** \
 * **Duration** \
 * **Set**
 */
export type DateTimeSubKind = string;

/** Known values of {@link TemporalModifier} that the service accepts. */
export enum KnownTemporalModifier {
  /** AfterApprox */
  AfterApprox = "AfterApprox",
  /** Before */
  Before = "Before",
  /** BeforeStart */
  BeforeStart = "BeforeStart",
  /** Approx */
  Approx = "Approx",
  /** ReferenceUndefined */
  ReferenceUndefined = "ReferenceUndefined",
  /** SinceEnd */
  SinceEnd = "SinceEnd",
  /** AfterMid */
  AfterMid = "AfterMid",
  /** Start */
  Start = "Start",
  /** After */
  After = "After",
  /** BeforeEnd */
  BeforeEnd = "BeforeEnd",
  /** Until */
  Until = "Until",
  /** End */
  End = "End",
  /** Less */
  Less = "Less",
  /** Since */
  Since = "Since",
  /** AfterStart */
  AfterStart = "AfterStart",
  /** BeforeApprox */
  BeforeApprox = "BeforeApprox",
  /** Mid */
  Mid = "Mid",
  /** More */
  More = "More"
}

/**
 * Defines values for TemporalModifier. \
 * {@link KnownTemporalModifier} can be used interchangeably with TemporalModifier,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **AfterApprox** \
 * **Before** \
 * **BeforeStart** \
 * **Approx** \
 * **ReferenceUndefined** \
 * **SinceEnd** \
 * **AfterMid** \
 * **Start** \
 * **After** \
 * **BeforeEnd** \
 * **Until** \
 * **End** \
 * **Less** \
 * **Since** \
 * **AfterStart** \
 * **BeforeApprox** \
 * **Mid** \
 * **More**
 */
export type TemporalModifier = string;

/** Known values of {@link NumberKind} that the service accepts. */
export enum KnownNumberKind {
  /** Integer */
  Integer = "Integer",
  /** Decimal */
  Decimal = "Decimal",
  /** Power */
  Power = "Power",
  /** Fraction */
  Fraction = "Fraction",
  /** Percent */
  Percent = "Percent",
  /** Unspecified */
  Unspecified = "Unspecified"
}

/**
 * Defines values for NumberKind. \
 * {@link KnownNumberKind} can be used interchangeably with NumberKind,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Integer** \
 * **Decimal** \
 * **Power** \
 * **Fraction** \
 * **Percent** \
 * **Unspecified**
 */
export type NumberKind = string;

/** Known values of {@link RelativeTo} that the service accepts. */
export enum KnownRelativeTo {
  /** Current */
  Current = "Current",
  /** End */
  End = "End",
  /** Start */
  Start = "Start"
}

/**
 * Defines values for RelativeTo. \
 * {@link KnownRelativeTo} can be used interchangeably with RelativeTo,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Current** \
 * **End** \
 * **Start**
 */
export type RelativeTo = string;

/** Known values of {@link RangeKind} that the service accepts. */
export enum KnownRangeKind {
  /** Number */
  Number = "Number",
  /** Speed */
  Speed = "Speed",
  /** Weight */
  Weight = "Weight",
  /** Length */
  Length = "Length",
  /** Volume */
  Volume = "Volume",
  /** Area */
  Area = "Area",
  /** Age */
  Age = "Age",
  /** Information */
  Information = "Information",
  /** Temperature */
  Temperature = "Temperature",
  /** Currency */
  Currency = "Currency"
}

/**
 * Defines values for RangeKind. \
 * {@link KnownRangeKind} can be used interchangeably with RangeKind,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Number** \
 * **Speed** \
 * **Weight** \
 * **Length** \
 * **Volume** \
 * **Area** \
 * **Age** \
 * **Information** \
 * **Temperature** \
 * **Currency**
 */
export type RangeKind = string;
/** Defines values for EntityConditionality. */
export type EntityConditionality = "hypothetical" | "conditional";
/** Defines values for EntityCertainty. */
export type EntityCertainty =
  | "positive"
  | "positivePossible"
  | "neutralPossible"
  | "negativePossible"
  | "negative";
/** Defines values for EntityAssociation. */
export type EntityAssociation = "subject" | "other";
/** Defines values for DocumentSentimentLabel. */
export type DocumentSentimentLabel =
  | "positive"
  | "neutral"
  | "negative"
  | "mixed";
/** Defines values for SentenceSentimentLabel. */
export type SentenceSentimentLabel = "positive" | "neutral" | "negative";
/** Defines values for TokenSentimentLabel. */
export type TokenSentimentLabel = "positive" | "mixed" | "negative";
/** Defines values for TargetRelationType. */
export type TargetRelationType = "assessment" | "target";
/** Defines values for PhraseControlStrategy. */
export type PhraseControlStrategy = "encourage" | "discourage" | "disallow";

/** Optional parameters. */
export interface AnalyzeOptionalParams extends coreClient.OperationOptions {
  /** (Optional) if set to true, response will contain request and document level statistics. */
  includeStatistics?: boolean;
}

/** Contains response data for the analyze operation. */
export type AnalyzeResponse = AnalyzeTextTaskResultUnion;

/** Optional parameters. */
export interface AnalyzeBatchOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the analyzeBatch operation. */
export type AnalyzeBatchResponse = GeneratedClientAnalyzeBatchHeaders;

/** Optional parameters. */
export interface AnalyzeTextJobStatusOptionalParams
  extends coreClient.OperationOptions {
  /** (Optional) if set to true, response will contain request and document level statistics. */
  includeStatistics?: boolean;
  /** The maximum number of resources to return from the collection. */
  top?: number;
  /** An offset into the collection of the first resource to be returned. */
  skip?: number;
}

/** Contains response data for the jobStatus operation. */
export type AnalyzeTextJobStatusResponse = AnalyzeTextJobState;

/** Optional parameters. */
export interface AnalyzeTextCancelJobOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the cancelJob operation. */
export type AnalyzeTextCancelJobResponse = AnalyzeTextCancelJobHeaders;

/** Optional parameters. */
export interface GeneratedClientOptionalParams
  extends coreClient.ServiceClientOptions {
  /** Api Version */
  apiVersion?: string;
  /** Overrides client endpoint. */
  endpoint?: string;
}
